<template>
    <div class="ecommerce-onboarding">
        <b-container fluid class="d-flex align-items-start py-4">
            <!-- Loading State -->
            <b-row v-if="loadingSettings" class="w-100" align-h="center">
                <b-col cols="12" class="text-center">
                    <b-spinner variant="primary" style="width: 3rem; height: 3rem;"></b-spinner>
                    <p class="mt-3 text-muted">{{ $t('ecommerce.loading') }}</p>
                </b-col>
            </b-row>

            <!-- Onboarding Content -->
            <b-row v-else class="w-100" align-h="center">
                <b-col cols="12" xl="11">
                    <!-- Progress Indicator -->
                    <div class="progress-bar-container mb-5">
                        <div class="progress-steps">
                            <div 
                                v-for="(step, index) in steps" 
                                :key="index"
                                class="progress-step"
                                :class="{ 
                                    active: currentStep === index, 
                                    completed: currentStep > index 
                                }"
                            >
                                <div class="step-circle">
                                    <b-icon v-if="currentStep > index" icon="check" />
                                    <span v-else>{{ index + 1 }}</span>
                                </div>
                                <div class="step-label">{{ step.label }}</div>
                            </div>
                        </div>
                    </div>

                    <!-- Step Content -->
                    <b-card class="onboarding-card">
                        <!-- Step 0: Welcome -->
                        <div v-if="currentStep === 0" class="onboarding-step">
                            <div class="text-center mb-4">
                                <div class="step-icon mb-3">
                                    <b-icon icon="cart-check" font-scale="3.5" variant="primary"></b-icon>
                                </div>
                                <h2 class="step-title">{{ $t('ecommerce.welcome.title') }}</h2>
                                <p class="step-description">{{ $t('ecommerce.welcome.description') }}</p>
                            </div>

                            <div class="features-list">
                                <div class="feature-item">
                                    <b-icon icon="check-circle-fill" variant="success"></b-icon>
                                    <span>{{ $t('ecommerce.welcome.feature1') }}</span>
                                </div>
                                <div class="feature-item">
                                    <b-icon icon="check-circle-fill" variant="success"></b-icon>
                                    <span>{{ $t('ecommerce.welcome.feature2') }}</span>
                                </div>
                                <div class="feature-item">
                                    <b-icon icon="check-circle-fill" variant="success"></b-icon>
                                    <span>{{ $t('ecommerce.welcome.feature3') }}</span>
                                </div>
                                <div class="feature-item">
                                    <b-icon icon="check-circle-fill" variant="success"></b-icon>
                                    <span>{{ $t('ecommerce.welcome.feature4') }}</span>
                                </div>
                                <div class="feature-item">
                                    <b-icon icon="check-circle-fill" variant="success"></b-icon>
                                    <span>{{ $t('ecommerce.welcome.feature5') }}</span>
                                </div>
                            </div>

                            <b-alert show variant="info" class="mt-4 text-center">
                                <h5 class="mb-2">{{ $t('ecommerce.welcome.pricingTitle') }}</h5>
                                <div class="pricing-amount">
                                    <b-spinner v-if="loadingPrice" small class="mr-2"></b-spinner>
                                    <template v-else-if="integrationPrice">
                                        <strong style="font-size: 2rem;">{{ integrationPrice.formatted }}</strong>
                                        <span class="text-muted" style="font-size: 1rem;"> / {{ $t('ecommerce.welcome.perMonth') }}</span>
                                    </template>
                                    <template v-else>
                                        <strong style="font-size: 2rem;">—</strong>
                                        <span class="text-muted" style="font-size: 1rem;"> / {{ $t('ecommerce.welcome.perMonth') }}</span>
                                    </template>
                                </div>
                            </b-alert>
                        </div>

                        <!-- Step 2: Store Configuration -->
                        <div v-if="currentStep === 2" class="onboarding-step">
                            <div class="text-center mb-4">
                                <div class="step-icon mb-3">
                                    <b-icon icon="shop" font-scale="3" variant="primary"></b-icon>
                                </div>
                                <h2 class="step-title">{{ $t('ecommerce.store.title') }}</h2>
                                <p class="step-description">{{ $t('ecommerce.store.description') }}</p>
                            </div>

                            <b-form @submit.prevent="nextStep">
                                <b-form-group label-for="store-name">
                                    <template #label>
                                        {{ $t('ecommerce.store.storeName') }} <span class="text-danger">*</span>
                                    </template>
                                    <b-form-input
                                        id="store-name"
                                        v-model="formData.storeName"
                                        :placeholder="$t('ecommerce.store.storeNamePlaceholder')"
                                        required
                                        size="lg"
                                    />
                                </b-form-group>

                                <b-form-group label-for="store-url">
                                    <template #label>
                                        {{ $t('ecommerce.store.storeUrl') }} <span class="text-danger">*</span>
                                    </template>
                                    <b-input-group size="lg">
                                        <b-form-input
                                            id="store-url"
                                            v-model="formData.storeUrl"
                                            :placeholder="$t('ecommerce.store.storeUrlPlaceholder')"
                                            :state="domainValidationError ? false : (domainValidationSuccess ? true : null)"
                                            required
                                        />
                                        <b-input-group-append>
                                            <b-input-group-text>
                                                <b-spinner small v-if="validatingDomain"></b-spinner>
                                                <span v-else>.{{ shopDomain }}</span>
                                            </b-input-group-text>
                                        </b-input-group-append>
                                    </b-input-group>
                                    <b-form-invalid-feedback :state="!domainValidationError">
                                        {{ domainValidationError }}
                                    </b-form-invalid-feedback>
                                </b-form-group>

                                <b-form-group label-for="store-logo">
                                    <template #label>
                                        {{ $t('ecommerce.store.logo') }}
                                    </template>
                                    <b-form-file
                                        id="store-logo"
                                        v-model="logoFile"
                                        :placeholder="$t('ecommerce.store.logoPlaceholder')"
                                        :drop-placeholder="$t('ecommerce.store.logoDropPlaceholder')"
                                        accept="image/*"
                                        size="lg"
                                        @change="onLogoChange"
                                    ></b-form-file>
                                    <b-form-text v-if="!formData.logoBase64">{{ $t('ecommerce.store.logoFallbackHint') }}</b-form-text>
                                    <b-alert v-if="logoSizeError" show variant="danger" class="mt-2 py-2 px-3">
                                        {{ $t('ecommerce.store.logoSizeError') }}
                                    </b-alert>
                                    <div v-if="formData.logoBase64" class="mt-2">
                                        <b-alert show variant="success" class="py-2 px-3 d-flex align-items-center">
                                            <b-icon icon="check-circle-fill" class="mr-2"></b-icon>
                                            <span>{{ $t('ecommerce.store.logoUploaded') }}</span>
                                            <b-img :src="formData.logoBase64" style="height: 50px; width: auto;" class="ml-auto" thumbnail></b-img>
                                        </b-alert>
                                    </div>
                                </b-form-group>

                                <b-form-group label-for="currency">
                                    <template #label>
                                        {{ $t('ecommerce.store.currency') }} <span class="text-danger">*</span>
                                    </template>
                                    <b-form-select
                                        id="currency"
                                        v-model="formData.currency"
                                        :options="currencyOptions"
                                        :disabled="isSerbian"
                                        required
                                        size="lg"
                                    />
                                </b-form-group>

                                <b-form-group label-for="language">
                                    <template #label>
                                        {{ $t('ecommerce.store.language') }} <span class="text-danger">*</span>
                                    </template>
                                    <b-form-select
                                        id="language"
                                        v-model="formData.language"
                                        :options="languageOptions"
                                        required
                                        size="lg"
                                    />
                                </b-form-group>

                                <b-form-group label-for="merchant-description">
                                    <template #label>
                                        {{ $t('ecommerce.store.merchantDescription') }} <span class="text-danger">*</span>
                                    </template>
                                    <b-form-select
                                        id="merchant-description"
                                        v-model="formData.merchantDescription"
                                        :options="merchantDescriptionOptionsFormatted"
                                        required
                                        size="lg"
                                    />
                                </b-form-group>
                            </b-form>
                        </div>

                        <!-- Step 1: Company Info -->
                        <div v-if="currentStep === 1" class="onboarding-step">
                            <div class="text-center mb-4">
                                <div class="step-icon mb-3">
                                    <b-icon icon="building" font-scale="3" variant="primary"></b-icon>
                                </div>
                                <h2 class="step-title">{{ $t('ecommerce.companyInfo.title') }}</h2>
                                <p class="step-description">{{ $t('ecommerce.companyInfo.description') }}</p>
                            </div>

                            <b-form @submit.prevent="nextStep">
                                <!-- Country selector — always shown; disabled when predefined by integration config -->
                                <b-form-group label-for="company-country">
                                    <template #label>
                                        {{ $t('ecommerce.companyInfo.country') }} <span class="text-danger">*</span>
                                    </template>
                                    <b-form-select
                                        id="company-country"
                                        v-model="formData.companyInfo.country"
                                        :options="countryOptions"
                                        :disabled="!!predefinedCountry"
                                        required
                                        size="lg"
                                    />
                                </b-form-group>

                                <!-- RS-only: PIB lookup -->
                                <b-form-group v-if="isSerbian" label-for="company-pib">
                                    <template #label>
                                        {{ $t('ecommerce.companyInfo.pib') }} <span class="text-danger">*</span>
                                    </template>
                                    <b-input-group size="lg">
                                        <b-form-input
                                            id="company-pib"
                                            v-model="formData.companyInfo.pib"
                                            :placeholder="$t('ecommerce.companyInfo.pibPlaceholder')"
                                            :state="pibError ? false : null"
                                            type="text"
                                            maxlength="9"
                                            required
                                            @input="onPibInput"
                                        />
                                        <b-input-group-append>
                                            <b-input-group-text>
                                                <b-spinner small v-if="fetchingCompanyData"></b-spinner>
                                                <b-icon v-else-if="formData.companyInfo.pib.length === 9 && !pibError" icon="check-circle-fill" variant="success"></b-icon>
                                            </b-input-group-text>
                                        </b-input-group-append>
                                    </b-input-group>
                                    <b-form-invalid-feedback :state="!pibError">
                                        {{ pibError }}
                                    </b-form-invalid-feedback>
                                    <b-form-text v-if="!pibError">{{ $t('ecommerce.companyInfo.pibHint') }}</b-form-text>
                                </b-form-group>

                                <!-- RS-only: Legal name -->
                                <b-form-group v-if="isSerbian" label-for="legal-name">
                                    <template #label>
                                        {{ $t('ecommerce.companyInfo.legalName') }} <span class="text-danger">*</span>
                                    </template>
                                    <b-form-input
                                        id="legal-name"
                                        v-model="formData.companyInfo.legalName"
                                        :placeholder="$t('ecommerce.companyInfo.legalNamePlaceholder')"
                                        required
                                        size="lg"
                                    />
                                </b-form-group>

                                <!-- All: Company (trade) name -->
                                <b-form-group label-for="company-name">
                                    <template #label>
                                        {{ $t('ecommerce.companyInfo.companyName') }} <span class="text-danger">*</span>
                                    </template>
                                    <b-form-input
                                        id="company-name"
                                        v-model="formData.companyInfo.companyName"
                                        :placeholder="$t('ecommerce.companyInfo.companyNamePlaceholder')"
                                        :maxlength="40"
                                        required
                                        size="lg"
                                    />
                                    <b-form-text>{{ $t('ecommerce.companyInfo.companyNameHint') }}</b-form-text>
                                </b-form-group>

                                <!-- RS-only: Address -->
                                <b-form-group v-if="isSerbian" label-for="company-address">
                                    <template #label>
                                        {{ $t('ecommerce.companyInfo.companyAddress') }} <span class="text-danger">*</span>
                                    </template>
                                    <b-form-input
                                        id="company-address"
                                        v-model="formData.companyInfo.companyAddress"
                                        :placeholder="$t('ecommerce.companyInfo.companyAddressPlaceholder')"
                                        required
                                        size="lg"
                                    />
                                </b-form-group>

                                <!-- RS-only: City + Postal -->
                                <b-row v-if="isSerbian">
                                    <b-col md="8">
                                        <b-form-group label-for="company-city">
                                            <template #label>
                                                {{ $t('ecommerce.companyInfo.companyCity') }} <span class="text-danger">*</span>
                                            </template>
                                            <b-form-input
                                                id="company-city"
                                                v-model="formData.companyInfo.companyCity"
                                                :placeholder="$t('ecommerce.companyInfo.companyCityPlaceholder')"
                                                required
                                                size="lg"
                                            />
                                        </b-form-group>
                                    </b-col>
                                    <b-col md="4">
                                        <b-form-group label-for="company-postal">
                                            <template #label>
                                                {{ $t('ecommerce.companyInfo.companyPostalCode') }} <span class="text-danger">*</span>
                                            </template>
                                            <b-form-input
                                                id="company-postal"
                                                v-model="formData.companyInfo.companyPostalCode"
                                                :placeholder="$t('ecommerce.companyInfo.companyPostalCodePlaceholder')"
                                                required
                                                size="lg"
                                            />
                                        </b-form-group>
                                    </b-col>
                                </b-row>

                                <!-- RS-only: Phone with +381 prefix -->
                                <b-form-group v-if="isSerbian" label-for="company-phone">
                                    <template #label>
                                        {{ $t('ecommerce.companyInfo.companyPhone') }} <span class="text-danger">*</span>
                                    </template>
                                    <b-input-group size="lg">
                                        <b-input-group-prepend>
                                            <b-input-group-text>+381</b-input-group-text>
                                        </b-input-group-prepend>
                                        <b-form-input
                                            id="company-phone"
                                            v-model="formData.companyInfo.companyPhone"
                                            :placeholder="$t('ecommerce.companyInfo.companyPhonePlaceholder')"
                                            required
                                            size="lg"
                                        />
                                    </b-input-group>
                                </b-form-group>

                                <!-- All: Email -->
                                <b-form-group label-for="company-email">
                                    <template #label>
                                        {{ $t('ecommerce.companyInfo.companyEmail') }} <span class="text-danger">*</span>
                                    </template>
                                    <b-form-input
                                        id="company-email"
                                        v-model="formData.companyInfo.companyEmail"
                                        :placeholder="$t('ecommerce.companyInfo.companyEmailPlaceholder')"
                                        type="email"
                                        required
                                        size="lg"
                                    />
                                </b-form-group>

                                <!-- RS-only: Legal representative -->
                                <b-form-group v-if="isSerbian" label-for="legal-representative">
                                    <template #label>
                                        {{ $t('ecommerce.companyInfo.legalRepresentative') }} <span class="text-danger">*</span>
                                    </template>
                                    <b-form-input
                                        id="legal-representative"
                                        v-model="formData.companyInfo.legalRepresentative"
                                        :placeholder="$t('ecommerce.companyInfo.legalRepresentativePlaceholder')"
                                        required
                                        size="lg"
                                    />
                                </b-form-group>

                                <!-- RS-only: MB + Activity Code (auto-filled) -->
                                <b-row v-if="isSerbian">
                                    <b-col md="6">
                                        <b-form-group :label="$t('ecommerce.companyInfo.mb')" label-for="company-mb">
                                            <b-form-input
                                                id="company-mb"
                                                v-model="formData.companyInfo.mb"
                                                :placeholder="$t('ecommerce.companyInfo.mbPlaceholder')"
                                                size="lg"
                                                readonly
                                            />
                                        </b-form-group>
                                    </b-col>
                                    <b-col md="6">
                                        <b-form-group :label="$t('ecommerce.companyInfo.activityCode')" label-for="company-activity">
                                            <b-form-input
                                                id="company-activity"
                                                v-model="formData.companyInfo.activityCode"
                                                :placeholder="$t('ecommerce.companyInfo.activityCodePlaceholder')"
                                                size="lg"
                                                readonly
                                            />
                                        </b-form-group>
                                    </b-col>
                                </b-row>

                                <!-- PDV Registration -->
                                <b-form-group label-for="company-pdv">
                                    <template #label>
                                        {{ $t('ecommerce.companyInfo.pdvQuestion') }} <span class="text-danger">*</span>
                                    </template>
                                    <b-button-group size="lg" class="w-100">
                                        <b-button
                                            :variant="formData.companyInfo.pdv === true ? 'primary' : 'outline-primary'"
                                            @click="formData.companyInfo.pdv = true"
                                        >
                                            {{ $t('ecommerce.companyInfo.pdvYes') }}
                                        </b-button>
                                        <b-button
                                            :variant="formData.companyInfo.pdv === false ? 'primary' : 'outline-primary'"
                                            @click="formData.companyInfo.pdv = false"
                                        >
                                            {{ $t('ecommerce.companyInfo.pdvNo') }}
                                        </b-button>
                                    </b-button-group>
                                </b-form-group>
                            </b-form>
                        </div>

                        <!-- Step 3: Product Type & Shipping Methods -->
                        <div v-if="currentStep === 3" class="onboarding-step">
                            <div class="text-center mb-4">
                                <div class="step-icon mb-3">
                                    <b-icon icon="box-seam" font-scale="3" variant="primary"></b-icon>
                                </div>
                                <h2 class="step-title">{{ $t('ecommerce.shipping.title') }}</h2>
                                <p class="step-description">{{ $t('ecommerce.shipping.description') }}</p>
                            </div>

                            <b-form-group class="mb-4">
                                <template #label>
                                    {{ $t('ecommerce.shipping.productType') }} <span class="text-danger">*</span>
                                </template>
                                <div class="payment-type-selection">
                                    <div
                                        v-for="type in productTypes"
                                        :key="type.value"
                                        class="payment-type-box"
                                        :class="{ 'selected': formData.productType === type.value }"
                                        @click="formData.productType = type.value"
                                    >
                                        <b-icon :icon="type.icon" font-scale="2" class="mb-2"></b-icon>
                                        <div class="payment-type-name">{{ $t(type.textKey) }}</div>
                                    </div>
                                </div>
                            </b-form-group>

                            <b-form-group v-if="formData.productType === 'physical'" class="mb-4">
                                <template #label>
                                    {{ $t('ecommerce.shipping.methods') }} <span class="text-danger">*</span>
                                </template>
                                <div class="payment-type-selection">
                                    <div
                                        v-for="method in shippingMethods"
                                        :key="method.value"
                                        class="payment-type-box"
                                        :class="{ 'selected': formData.shippingMethods.includes(method.value) }"
                                        @click="toggleShippingMethod(method.value)"
                                    >
                                        <b-icon :icon="method.icon" font-scale="2" class="mb-2"></b-icon>
                                        <div class="payment-type-name">{{ $t(method.textKey) }}</div>
                                    </div>
                                </div>
                            </b-form-group>
                        </div>

                        <!-- Step 4: Payment Methods -->
                        <div v-if="currentStep === 4" class="onboarding-step">
                            <div class="text-center mb-4">
                                <div class="step-icon mb-3">
                                    <b-icon icon="credit-card" font-scale="3" variant="primary"></b-icon>
                                </div>
                                <h2 class="step-title">{{ $t('ecommerce.payment.title') }}</h2>
                                <p class="step-description">{{ $t('ecommerce.payment.description') }}</p>
                            </div>

                            <!-- Payment Type Selection -->
                            <b-form-group class="mb-4">
                                <template #label>
                                    {{ $t('ecommerce.payment.choosePaymentType') }} <span class="text-danger">*</span>
                                </template>
                                <div class="payment-type-selection">
                                    <div
                                        class="payment-type-box"
                                        :class="{ 'selected': formData.paymentMethods.includes('credit_card'), 'disabled': isMonriPartner }"
                                        @click="!isMonriPartner && togglePaymentMethod('credit_card')"
                                    >
                                        <b-icon icon="credit-card" font-scale="2" class="mb-2"></b-icon>
                                        <div class="payment-type-name">
                                            {{ $t('ecommerce.payment.paymentCard') }}
                                            <b-badge v-if="isMonriPartner" variant="primary" class="ml-1">
                                                {{ $t('ecommerce.payment.required') }}
                                            </b-badge>

                                            <div class="payment-card-logos">
                                                <span class="payment-logo-pill">
                                                    <img :src="masterCardlogo" alt="Mastercard" class="payment-card-logo" />
                                                </span>
                                                <span class="payment-logo-pill">
                                                    <img :src="visaLogo" alt="Visa" class="payment-card-logo" />
                                                </span>
                                                <span v-if="isSerbian" class="payment-logo-pill">
                                                    <img :src="ipsLogo" alt="IPS" class="payment-card-logo" />
                                                </span>
                                                <span class="payment-logo-pill payment-logo-pill--compact">
                                                    <img :src="applePayLogo" alt="Apple Pay" class="payment-card-logo" />
                                                </span>
                                                <span class="payment-logo-pill">
                                                    <img :src="googlePayLogo" alt="Google Pay" class="payment-card-logo" />
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div
                                        class="payment-type-box"
                                        :class="{ 'selected': formData.paymentMethods.includes('bank_transfer') }"
                                        @click="togglePaymentMethod('bank_transfer')"
                                    >
                                        <b-icon icon="bank" font-scale="2" class="mb-2"></b-icon>
                                        <div class="payment-type-name">{{ $t('ecommerce.payment.bankTransfer') }}</div>
                                    </div>
                                    <div
                                        class="payment-type-box"
                                        :class="{ 'selected': formData.paymentMethods.includes('cash_on_delivery') }"
                                        @click="togglePaymentMethod('cash_on_delivery')"
                                    >
                                        <b-icon icon="cash" font-scale="2" class="mb-2"></b-icon>
                                        <div class="payment-type-name">{{ $t('ecommerce.payment.cashOnDelivery') }}</div>
                                    </div>
                                </div>
                            </b-form-group>

                            <!-- Payment Provider Selection (shown when credit card is selected) -->
                            <b-form-group v-if="formData.paymentMethods.includes('credit_card')" class="mb-4">
                                <template #label>
                                    {{ $t('ecommerce.payment.chooseProvider') }} <span class="text-danger">*</span>
                                </template>
                                <div class="payment-type-selection">
                                    <div
                                        v-if="!(isMonriPartner && isSerbian)"
                                        class="payment-type-box"
                                        :class="{ 'selected': formData.creditCardProvider === 'monri', 'disabled': isMonriPartner }"
                                        @click="!isMonriPartner && (formData.creditCardProvider = 'monri')"
                                    >
                                        <img :src="monriLogoBase64" alt="Monri" class="provider-logo" />
                                        <b-badge v-if="isMonriPartner" variant="primary" class="mt-2">{{ $t('ecommerce.payment.required') }}</b-badge>
                                    </div>
                                    <div
                                        v-if="isMonriPartner && isSerbian"
                                        class="payment-type-box selected disabled"
                                    >
                                        <img :src="chipcardLogoBase64" alt="Chipcard" class="provider-logo provider-logo--wide" />
                                        <b-badge variant="primary" class="mt-2">{{ $t('ecommerce.payment.required') }}</b-badge>
                                    </div>
                                </div>
                            </b-form-group>

                            <!-- Monri Credentials (shown when Monri is chosen as provider) -->
                            <b-form-group v-if="formData.paymentMethods.includes('credit_card') && formData.creditCardProvider === 'monri' && !isMonriPartner" class="mb-4">
                                <template #label>
                                    {{ $t('ecommerce.payment.monriCredentials') }}
                                </template>
                                <b-form-checkbox v-if="!isMonriPartner" v-model="formData.monriSetupLater" class="mb-3">
                                    {{ $t('ecommerce.payment.setupLater') }}
                                </b-form-checkbox>
                                <template v-if="!formData.monriSetupLater && !isMonriPartner">
                                    <b-form-group :label="$t('ecommerce.payment.merchantKey')" label-for="monri-merchant-key" class="mb-3">
                                        <b-form-input
                                            id="monri-merchant-key"
                                            v-model="formData.monriMerchantKey"
                                            :placeholder="$t('ecommerce.payment.merchantKeyPlaceholder')"
                                        />
                                    </b-form-group>
                                    <b-form-group :label="$t('ecommerce.payment.authenticityToken')" label-for="monri-auth-token">
                                        <b-form-input
                                            id="monri-auth-token"
                                            v-model="formData.monriAuthenticityToken"
                                            :placeholder="$t('ecommerce.payment.authenticityTokenPlaceholder')"
                                            type="password"
                                        />
                                    </b-form-group>
                                </template>
                                <small v-if="isMonriPartner" class="text-muted">{{ $t('ecommerce.payment.monriPartnerCredentialsInfo') }}</small>
                            </b-form-group>

                            <!-- Bank Transfer Instructions Upload -->
                            <b-form-group v-if="formData.paymentMethods.includes('bank_transfer')" class="mb-4">
                                <template #label>
                                    {{ $t('ecommerce.payment.bankTransferInstruction') }}
                                </template>
                                <div v-if="!formData.bankTransferInstructionPdf" class="upload-box dashed-box" @click="$refs.bankInstructionInput.$el.querySelector('input').click()">
                                    <b-icon icon="file-earmark-text" font-scale="2" class="mb-2 text-muted"></b-icon>
                                    <p class="mb-1 text-primary">{{ $t('ecommerce.payment.bankTransferInstructionUploadPrompt') }}</p>
                                    <small class="text-muted">{{ $t('ecommerce.payment.bankTransferInstructionHint') }}</small>
                                </div>
                                <div v-else>
                                    <b-alert show variant="success" class="mb-0 py-2 px-3 d-flex align-items-center">
                                        <b-icon icon="file-earmark-check-fill" class="mr-2"></b-icon>
                                        <span>{{ $t('ecommerce.payment.bankTransferInstructionUploaded') }}</span>
                                        <b-button size="sm" variant="outline-danger" class="ml-auto" @click="formData.bankTransferInstructionPdf = ''; bankInstructionFile = null">
                                            <b-icon icon="trash"></b-icon>
                                        </b-button>
                                    </b-alert>
                                </div>
                                <b-form-file
                                    ref="bankInstructionInput"
                                    v-model="bankInstructionFile"
                                    accept=".pdf"
                                    class="d-none"
                                    @change="onBankInstructionChange"
                                />
                            </b-form-group>

                            <!-- Credit Card Receiving Method Selection (only when Chipcard or AIK Bank configured) -->
                            <b-form-group v-if="formData.paymentMethods.includes('credit_card') && formData.creditCardProvider === 'monri' && (isChipcardPartner || isAikBank)" class="mb-4">
                                <template #label>
                                    {{ $t('ecommerce.payment.chooseCreditCardReceiving') }} <span class="text-danger">*</span>
                                </template>
                                <p class="text-muted small mb-3">{{ $t('ecommerce.payment.creditCardReceivingDescription') }}</p>
                                <div class="payment-type-selection">
                                    <div
                                        class="payment-type-box"
                                        :class="{ 'selected': formData.creditCardReceivingMethod === 'chip_card' }"
                                        @click="formData.creditCardReceivingMethod = 'chip_card'; formData.companyInfo.selectedBankAccount = null"
                                    >
                                        <img :src="chipcardLogoBase64" alt="Chipcard" class="provider-logo provider-logo--wide" />
                                    </div>
                                    <div
                                        class="payment-type-box"
                                        :class="{ 'selected': formData.creditCardReceivingMethod === 'bank_transfer' }"
                                        @click="selectBankTransferReceiving"
                                    >
                                        <img v-if="isAikBank" :src="aikBankLogoBase64" alt="AIK Bank" class="provider-logo provider-logo--wide" />
                                        <template v-else>
                                            <b-icon icon="bank" font-scale="2" class="mb-2"></b-icon>
                                            <div class="payment-type-name">{{ $t('ecommerce.payment.bank') }}</div>
                                        </template>
                                    </div>
                                </div>
                                <div class="mt-2">
                                    <b-button variant="link" size="sm" @click="showChipcardInfoModal = true">
                                        {{ $t('ecommerce.payment.whatIsChipcard') }}
                                    </b-button>
                                </div>
                            </b-form-group>

                            <!-- Chipcard Info Dialog -->
                            <main-dialog
                                :show="showChipcardInfoModal"
                                :title="$t('ecommerce.payment.chipcardModalTitle')"
                                @close="showChipcardInfoModal = false"
                            >
                                <p>{{ $t('ecommerce.payment.chipcardInfo1') }}</p>
                                <p>{{ $t('ecommerce.payment.chipcardInfoBusinessLines') }}</p>
                                <ul>
                                    <li>{{ $t('ecommerce.payment.chipcardLine1') }}</li>
                                    <li>{{ $t('ecommerce.payment.chipcardLine2') }}</li>
                                </ul>
                                <p>{{ $t('ecommerce.payment.chipcardInfo2') }}</p>
                                <template #footer>
                                    <b-button variant="secondary" @click="showChipcardInfoModal = false">{{ $t('close') }}</b-button>
                                </template>
                            </main-dialog>

                            <!-- Bank Account Selection (only when bank transfer receiving method is chosen) -->
                            <b-form-group v-if="formData.paymentMethods.includes('credit_card') && formData.creditCardProvider === 'monri' && (isChipcardPartner || isAikBank) && formData.creditCardReceivingMethod === 'bank_transfer'" label-for="bank-account" class="mt-4">
                                <template #label>
                                    {{ $t('ecommerce.companyInfo.bankAccount') }} <span class="text-danger">*</span>
                                </template>
                                <p class="text-muted small mb-3">{{ $t('ecommerce.payment.bankAccountForCreditCard') }}</p>
                                
                                <!-- Show message if no filtered accounts available -->
                                <b-alert v-if="filteredBankAccounts.length === 0" show variant="info" class="text-center">
                                    <p class="mb-2">{{ $t('ecommerce.payment.noBankAccount') }}</p>
                                </b-alert>
                                
                                <!-- Show filtered bank accounts -->
                                <div v-else class="bank-accounts-container">
                                    <div
                                        v-for="(bank, index) in filteredBankAccounts"
                                        :key="index"
                                        class="bank-account-box"
                                        :class="{ 'selected': formData.companyInfo.selectedBankAccount === bank.originalIndex }"
                                        @click="formData.companyInfo.selectedBankAccount = bank.originalIndex"
                                    >
                                        <div class="bank-account-header">
                                            <b-icon icon="bank" class="mr-2"></b-icon>
                                            <strong>{{ bank.bank }}</strong>
                                        </div>
                                        <div class="bank-account-number">{{ bank.number }}</div>
                                        <div class="bank-account-status">
                                            <b-badge :variant="bank.status ? 'success' : 'secondary'">
                                                {{ bank.status ? $t('ecommerce.payment.bankAccountActive') : $t('ecommerce.payment.bankAccountInactive') }}
                                            </b-badge>
                                        </div>
                                    </div>
                                </div>
                            </b-form-group>

                        </div>

                        <!-- Step 5: Documentation -->
                        <div v-if="currentStep === 5" class="onboarding-step">
                            <div class="text-center mb-4">
                                <div class="step-icon mb-3">
                                    <b-icon icon="file-text" font-scale="3" variant="primary"></b-icon>
                                </div>
                                <h2 class="step-title">{{ $t('ecommerce.documentation.title') }}</h2>
                                <p class="step-description">{{ $t('ecommerce.documentation.description') }}</p>
                            </div>

                            <b-alert show variant="success" class="mb-4">
                                <b-icon icon="check-circle" class="mr-2"></b-icon>
                                {{ $t('ecommerce.documentation.autoGeneratedInfo') }}
                            </b-alert>

                            <!-- TOS Preview -->
                            <div class="tos-preview-container mb-4">
                                <b-alert show variant="info" class="mb-3">
                                    <b-icon icon="info-circle" class="mr-2"></b-icon>
                                    {{ $t('ecommerce.documentation.tosPreviewInfo') }}
                                </b-alert>
                                <div v-if="loadingTosPreview" class="text-center py-4">
                                    <b-spinner variant="primary"></b-spinner>
                                </div>
                                <iframe v-else-if="tosPreviewHtml" class="tos-preview-frame" :srcdoc="tosPreviewHtml" sandbox="allow-same-origin" frameborder="0"></iframe>
                                <div v-else class="text-center py-4 text-muted">
                                    <b-icon icon="exclamation-triangle" class="mr-2"></b-icon>
                                    {{ $t('ecommerce.documentation.tosPreviewError') }}
                                </div>
                            </div>

                            <div class="document-preview mb-3">
                                <h5>{{ $t('ecommerce.documentation.termsOfService') }}</h5>
                                <div v-if="!formData.termsOfService" class="upload-box dashed-box" @click="$refs.termsFileInput.$el.querySelector('input').click()">
                                    <b-icon icon="file-earmark-text" font-scale="2" class="mb-2 text-muted"></b-icon>
                                    <p class="text-muted mb-1">{{ $t('ecommerce.documentation.autoGeneratedPreview') }}</p>
                                    <hr class="my-3" style="width: 80%; border-top: 1px dashed #dee2e6;">
                                    <b-icon icon="cloud-upload" font-scale="1.5" class="mb-2 text-primary"></b-icon>
                                    <p class="mb-1 text-primary">{{ $t('ecommerce.documentation.uploadPrompt') }}</p>
                                    <small class="text-muted">{{ $t('ecommerce.documentation.uploadHint') }}</small>
                                </div>
                                <div v-else class="preview-box">
                                    <b-alert show variant="success" class="mb-0 py-2 px-3 d-flex align-items-center">
                                        <b-icon icon="file-earmark-check-fill" class="mr-2"></b-icon>
                                        <span>{{ $t('ecommerce.documentation.customFileUploaded') }}</span>
                                        <b-button size="sm" variant="outline-danger" class="ml-auto" @click="formData.termsOfService = ''; termsOfServiceFile = null; formData.useAutoGeneratedDocs = true">
                                            <b-icon icon="trash"></b-icon>
                                        </b-button>
                                    </b-alert>
                                </div>
                                <b-form-file
                                    ref="termsFileInput"
                                    v-model="termsOfServiceFile"
                                    accept=".pdf"
                                    class="d-none"
                                    @change="onTermsOfServiceChange"
                                ></b-form-file>
                            </div>

                        </div>

                        <!-- Step 6: Summary / Preview -->
                        <div v-if="currentStep === 6" class="onboarding-step">
                            <div class="text-center mb-5">
                                <div class="preview-success-icon mb-3">
                                    <b-icon icon="check-circle-fill" font-scale="4" variant="success"></b-icon>
                                </div>
                                <h2 class="step-title">{{ $t('ecommerce.summary.title') }}</h2>
                                <p class="step-description">{{ $t('ecommerce.summary.description') }}</p>
                            </div>

                            <!-- Store Hero Banner -->
                            <div class="preview-store-banner mb-4">
                                <div class="preview-store-banner-logo">
                                    <img v-if="formData.logoBase64" :src="formData.logoBase64" alt="Store Logo" class="preview-banner-logo-img" />
                                    <div v-else class="preview-banner-logo-placeholder">
                                        <b-icon icon="shop" font-scale="2.5" variant="secondary"></b-icon>
                                    </div>
                                </div>
                                <div class="preview-store-banner-info">
                                    <div class="preview-store-name">{{ formData.storeName || formData.companyInfo.companyName || formData.storeUrl }}</div>
                                    <a :href="fullStoreDomain" target="_blank" rel="noopener" class="preview-store-url-link">
                                        <b-icon icon="globe" class="mr-1"></b-icon>{{ fullStoreDomain }}
                                    </a>
                                    <div class="preview-store-badges mt-2">
                                        <b-badge variant="primary" class="mr-1">{{ formData.currency }}</b-badge>
                                        <b-badge variant="secondary" class="mr-1">{{ getLanguageText(formData.language) }}</b-badge>
                                        <b-badge v-if="formData.productType" variant="light" class="mr-1">
                                            <b-icon :icon="getProductTypeIcon(formData.productType)" class="mr-1"></b-icon>
                                            {{ getProductTypeLabel(formData.productType) }}
                                        </b-badge>
                                    </div>
                                </div>
                            </div>

                            <!-- Preview Cards Grid -->
                            <div class="preview-cards-grid">

                                <!-- Company Info Card -->
                                <div class="preview-card">
                                    <div class="preview-card-header">
                                        <div class="preview-card-header-icon">
                                            <b-icon icon="building" variant="primary"></b-icon>
                                        </div>
                                        <h6>{{ $t('ecommerce.summary.companyInfo') }}</h6>
                                    </div>
                                    <div class="preview-card-body">
                                        <div class="preview-row">
                                            <span class="preview-label">{{ $t('ecommerce.companyInfo.country') }}</span>
                                            <span class="preview-value">{{ formData.companyInfo.country }}</span>
                                        </div>
                                        <div v-if="isSerbian" class="preview-row">
                                            <span class="preview-label">{{ $t('ecommerce.companyInfo.pib') }}</span>
                                            <span class="preview-value preview-mono">{{ formData.companyInfo.pib }}</span>
                                        </div>
                                        <div v-if="isSerbian" class="preview-row">
                                            <span class="preview-label">{{ $t('ecommerce.companyInfo.legalName') }}</span>
                                            <span class="preview-value">{{ formData.companyInfo.legalName }}</span>
                                        </div>
                                        <div class="preview-row">
                                            <span class="preview-label">{{ $t('ecommerce.companyInfo.companyName') }}</span>
                                            <span class="preview-value">{{ formData.companyInfo.companyName }}</span>
                                        </div>
                                        <div v-if="isSerbian" class="preview-row">
                                            <span class="preview-label">{{ $t('ecommerce.companyInfo.companyAddress') }}</span>
                                            <span class="preview-value">{{ formData.companyInfo.companyAddress }}</span>
                                        </div>
                                        <div v-if="isSerbian" class="preview-row">
                                            <span class="preview-label">{{ $t('ecommerce.companyInfo.companyCity') }}</span>
                                            <span class="preview-value">{{ formData.companyInfo.companyCity }}<span v-if="formData.companyInfo.companyPostalCode">, {{ formData.companyInfo.companyPostalCode }}</span></span>
                                        </div>
                                        <div v-if="isSerbian" class="preview-row">
                                            <span class="preview-label">{{ $t('ecommerce.companyInfo.companyPhone') }}</span>
                                            <span class="preview-value preview-mono">+381{{ formData.companyInfo.companyPhone }}</span>
                                        </div>
                                        <div class="preview-row">
                                            <span class="preview-label">{{ $t('ecommerce.companyInfo.companyEmail') }}</span>
                                            <span class="preview-value">{{ formData.companyInfo.companyEmail }}</span>
                                        </div>
                                        <div v-if="isSerbian" class="preview-row">
                                            <span class="preview-label">{{ $t('ecommerce.companyInfo.legalRepresentative') }}</span>
                                            <span class="preview-value">{{ formData.companyInfo.legalRepresentative }}</span>
                                        </div>
                                        <div v-if="isSerbian && formData.companyInfo.mb" class="preview-row">
                                            <span class="preview-label">{{ $t('ecommerce.companyInfo.mb') }}</span>
                                            <span class="preview-value preview-mono">{{ formData.companyInfo.mb }}</span>
                                        </div>
                                        <div v-if="isSerbian && formData.companyInfo.activityCode" class="preview-row">
                                            <span class="preview-label">{{ $t('ecommerce.companyInfo.activityCode') }}</span>
                                            <span class="preview-value preview-mono">{{ formData.companyInfo.activityCode }}</span>
                                        </div>
                                        <div class="preview-row">
                                            <span class="preview-label">{{ $t('ecommerce.companyInfo.pdvQuestion') }}</span>
                                            <span class="preview-value">{{ formData.companyInfo.pdv === true ? $t('ecommerce.companyInfo.pdvYes') : $t('ecommerce.companyInfo.pdvNo') }}</span>
                                        </div>
                                    </div>
                                </div>

                                <!-- Payment Methods Card -->
                                <div class="preview-card">
                                    <div class="preview-card-header">
                                        <div class="preview-card-header-icon">
                                            <b-icon icon="credit-card" variant="primary"></b-icon>
                                        </div>
                                        <h6>{{ $t('ecommerce.summary.paymentInfo') }}</h6>
                                    </div>
                                    <div class="preview-card-body">
                                        <div v-for="method in formData.paymentMethods" :key="method" class="preview-method-item">
                                            <div class="preview-method-icon">
                                                <b-icon :icon="getPaymentMethodIcon(method)" variant="primary"></b-icon>
                                            </div>
                                            <div class="preview-method-content">
                                                <span class="preview-method-name">{{ getPaymentMethodLabel(method) }}</span>
                                                <div v-if="method === 'credit_card' && formData.creditCardReceivingMethod" class="preview-method-sub">
                                                    <b-icon icon="arrow-return-right" font-scale="0.8" class="mr-1 text-muted"></b-icon>
                                                    <small class="text-muted">{{ $t('ecommerce.payment.paymentProcessor') }}: <strong>{{ getCreditCardReceivingLabel() }}</strong></small>
                                                    <span v-if="formData.creditCardReceivingMethod === 'bank_transfer' && formData.companyInfo.selectedBankAccount !== null && formData.companyInfo.bankAccounts[formData.companyInfo.selectedBankAccount]">
                                                        <br><small class="text-muted ml-3">{{ formData.companyInfo.bankAccounts[formData.companyInfo.selectedBankAccount].bank }} — <span class="preview-mono">{{ formData.companyInfo.bankAccounts[formData.companyInfo.selectedBankAccount].number }}</span></small>
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <div v-if="formData.paymentMethods.length === 0" class="text-muted small py-2">—</div>
                                    </div>
                                </div>

                                <!-- Shipping / Category Card -->
                                <div class="preview-card">
                                    <div class="preview-card-header">
                                        <div class="preview-card-header-icon">
                                            <b-icon :icon="formData.productType === 'physical' ? 'truck' : (formData.productType === 'digital' ? 'file-earmark-code' : 'tools')" variant="primary"></b-icon>
                                        </div>
                                        <h6>{{ $t('ecommerce.summary.shippingInfo') }}</h6>
                                    </div>
                                    <div class="preview-card-body">
                                        <div v-if="formData.productType" class="preview-row mb-2">
                                            <span class="preview-label">{{ $t('ecommerce.shipping.productType') }}</span>
                                            <b-badge variant="primary">{{ getProductTypeLabel(formData.productType) }}</b-badge>
                                        </div>
                                        <div v-if="formData.productType === 'physical' && formData.shippingMethods.length > 0">
                                            <div v-for="method in formData.shippingMethods" :key="method" class="preview-method-item">
                                                <div class="preview-method-icon">
                                                    <b-icon :icon="getShippingMethodIcon(method)" variant="primary"></b-icon>
                                                </div>
                                                <div class="preview-method-content">
                                                    <span class="preview-method-name">{{ getShippingMethodLabel(method) }}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div v-if="formData.productType !== 'physical'" class="text-muted small py-1">
                                            {{ $t('ecommerce.summary.noShippingRequired') }}
                                        </div>
                                    </div>
                                </div>

                                <!-- Legal Documents Card -->
                                <div class="preview-card">
                                    <div class="preview-card-header">
                                        <div class="preview-card-header-icon">
                                            <b-icon icon="file-text" variant="primary"></b-icon>
                                        </div>
                                        <h6>{{ $t('ecommerce.steps.documentation') }}</h6>
                                    </div>
                                    <div class="preview-card-body">
                                        <div class="preview-row">
                                            <span class="preview-label">{{ $t('ecommerce.documentation.termsOfService') }}</span>
                                            <b-badge :variant="formData.termsOfService ? 'success' : 'info'" class="preview-doc-badge">
                                                <b-icon :icon="formData.termsOfService ? 'check-circle-fill' : 'magic'" class="mr-1"></b-icon>
                                                {{ formData.termsOfService ? $t('ecommerce.documentation.customFileUploaded') : $t('ecommerce.summary.autoGenerated') }}
                                            </b-badge>
                                        </div>
                                        <div class="preview-row">
                                            <span class="preview-label">{{ $t('ecommerce.store.merchantDescription') }}</span>
                                            <span class="preview-value preview-value--sm">{{ getMerchantDescriptionText() }}</span>
                                        </div>
                                    </div>
                                </div>

                            </div>

                        </div>

                        <!-- Navigation Buttons -->
                        <div class="navigation-buttons mt-4">
                            <b-button
                                v-if="currentStep > 0"
                                variant="outline-secondary"
                                @click="prevStep"
                                size="lg"
                            >
                                <b-icon icon="arrow-left"></b-icon> {{ $t('back') }}
                            </b-button>

                            <b-button
                                v-if="currentStep < steps.length - 1"
                                variant="primary"
                                @click="nextStep"
                                :disabled="!canProceed"
                                size="lg"
                                class="ml-auto"
                            >
                                {{ $t('continue') }} <b-icon icon="arrow-right"></b-icon>
                            </b-button>

                            <b-button
                                v-if="currentStep === steps.length - 1"
                                variant="success"
                                @click="handleFinish"
                                :disabled="loading"
                                size="lg"
                                class="ml-auto"
                            >
                                <b-spinner small v-if="loading"></b-spinner>
                                {{ loading ? $t('saving') : $t('finish') }}
                            </b-button>
                        </div>

                        <div class="text-center mt-3">
                            <a href="#" class="help-link" @click.prevent="openSupportDialog">
                                {{ $t('ecommerce.needHelp') }}
                            </a>
                        </div>

                        <!-- Powered by footer -->
                        <div v-if="isAikBank" class="powered-by-footer mt-4 pt-3">
                            <div class="powered-by-text">Powered by</div>
                            <b-img :src="aikBankLogoBase64" alt="AIK Bank" class="powered-by-logo" />
                        </div>
                    </b-card>

                </b-col>
            </b-row>
        </b-container>

        <!-- Legal Confirmation Modal -->
        <main-dialog
            v-model="showLegalModal"
            :title="$t('ecommerce.welcome.confirmationTitle')"
            @ok="handleLegalConfirmation"
            :ok-title="$t('ecommerce.welcome.confirmButton')"
            :cancel-title="$t('cancel')"
            size="lg"
            centered
            no-close-on-backdrop
            no-close-on-esc
        >
            <b-alert show variant="warning" class="mb-0">
                <b-icon icon="exclamation-triangle-fill" class="mr-2"></b-icon>
                {{ $t('ecommerce.welcome.confirmationText') }}
            </b-alert>
        </main-dialog>

        <!-- Chipcard Preparation Modal (Monri partners) -->
        <main-dialog
            v-model="showChipcardModal"
            :title="$t('ecommerce.chipcard.title')"
            size="lg"
            centered
            no-close-on-backdrop
            no-close-on-esc
            @ok="handleChipcardConfirm"
            :ok-title="$t('ecommerce.chipcard.understood')"
            ok-only
        >
            <b-alert show variant="warning">
                <h6 class="mb-2">{{ $t('ecommerce.chipcard.prepareTitle') }}</h6>
                <p class="mb-3">{{ $t('ecommerce.chipcard.prepareDescription') }}</p>
                <ul class="mb-0 pl-3">
                    <li class="mb-2">{{ $t('ecommerce.chipcard.doc1') }}</li>
                    <li class="mb-2">{{ $t('ecommerce.chipcard.doc2') }}</li>
                    <li class="mb-2">{{ $t('ecommerce.chipcard.doc3') }}</li>
                    <li class="mb-0">{{ $t('ecommerce.chipcard.doc4') }}</li>
                </ul>
            </b-alert>
        </main-dialog>
    </div>
</template>

<script>
import Price from '../../../../plugins/protokol/lib/Price'

const ONBOARDING_CACHE_KEY = 'protokol-ecommerce-onboarding'

export default {
    name: 'EcommerceOnboarding',
    
    data() {
        return {
            currentStep: 0,
            loading: false,
            loadingSettings: true,
            loadingPrice: false,
            integrationPrice: null,
            priceHelper: null,
            validationErrors: {},
            validatingDomain: false,
            domainDebounceTimer: null,
            domainValidationError: null,
            domainValidationSuccess: false,
            fetchingCompanyData: false,
            pibError: null,
            showCustomDomainSetup: false,
            showLegalModal: false,
            showChipcardModal: false,
            showChipcardInfoModal: false,
            newDomain: '',
            storeUrlManuallyEdited: false,
            integration: null,
            paymentsIntegration: null,
            ecommSettings: null,
            customCategory: '',
            
            formData: {
                storeName: '',
                storeUrl: '',
                currency: 'USD',
                language: 'en',
                merchantDescription: '',
                productType: '',
                logoBase64: '',
                customDomains: [],
                creditCardProvider: null, // 'monri' | 'stripe' | 'braintree'
                monriMerchantKey: '',
                monriAuthenticityToken: '',
                monriSetupLater: false,
                creditCardReceivingMethod: null, // 'chip_card' or 'bank_transfer' - how to receive credit card payments
                paymentMethods: [],
                shippingMethods: [],
                bankTransferInstructionPdf: '',
                useAutoGeneratedDocs: true,
                termsOfService: '',
                companyInfo: {
                    country: '',
                    pib: '',
                    legalName: '',
                    companyName: '',
                    companyAddress: '',
                    companyCity: '',
                    companyPostalCode: '',
                    companyPhone: '',
                    companyEmail: '',
                    legalRepresentative: '',
                    activityCode: '',
                    mb: '',
                    bankAccounts: [],
                    selectedBankAccount: null,
                    pdv: null
                }
            },

            customDomain: '',
            logoFile: null,
            logoSizeError: false,
            bankInstructionFile: null,
            termsOfServiceFile: null,
            tosPreviewHtml: '',
            loadingTosPreview: false,
            monriLogoBase64: require('./monri-logo.png'),
            chipcardLogoBase64: require('./chipcard-logo.png'),
            visaLogo: require('./visa.png'),
            masterCardlogo: require('./mastercard.png'),
            ipsLogo: require('./ips.png'),
            applePayLogo: require('./apay.png'),
            googlePayLogo: require('./gpay.png'),

            countryOptions: [
                { value: '', text: this.$t('ecommerce.companyInfo.countryPlaceholder'), disabled: true },
                { value: 'RS', text: 'Serbia' },
                { value: 'AT', text: 'Austria' },
                { value: 'BA', text: 'Bosnia and Herzegovina' },
                { value: 'HR', text: 'Croatia' },
                { value: 'CZ', text: 'Czech Republic' },
                { value: 'DE', text: 'Germany' },
                { value: 'GR', text: 'Greece' },
                { value: 'HU', text: 'Hungary' },
                { value: 'IT', text: 'Italy' },
                { value: 'ME', text: 'Montenegro' },
                { value: 'MK', text: 'North Macedonia' },
                { value: 'PL', text: 'Poland' },
                { value: 'RO', text: 'Romania' },
                { value: 'SI', text: 'Slovenia' },
                { value: 'SK', text: 'Slovakia' },
                { value: 'FR', text: 'France' },
                { value: 'ES', text: 'Spain' },
                { value: 'NL', text: 'Netherlands' },
                { value: 'BE', text: 'Belgium' },
                { value: 'SE', text: 'Sweden' },
                { value: 'NO', text: 'Norway' },
                { value: 'DK', text: 'Denmark' },
                { value: 'FI', text: 'Finland' },
                { value: 'PT', text: 'Portugal' },
                { value: 'CH', text: 'Switzerland' },
                { value: 'GB', text: 'United Kingdom' },
                { value: 'US', text: 'United States' },
                { value: 'OTHER', text: 'Other' }
            ],

            currencyOptions: [
                { value: 'USD', text: 'USD - US Dollar' },
                { value: 'EUR', text: 'EUR - Euro' },
                { value: 'RSD', text: 'RSD - Serbian Dinar' },
                { value: 'GBP', text: 'GBP - British Pound' },
                { value: 'CHF', text: 'CHF - Swiss Franc' }
            ],

            languageOptions: [
                { value: 'en', text: 'English' },
                { value: 'sr', text: 'Serbian / Srpski' }
            ],

            merchantDescriptionOptions: [
                { value: 'online_store', text: 'Online Store', text_sr: 'Online prodavnica', mcc: ['5399', '5969'] },
                { value: 'clothing_apparel', text: 'Clothing & Apparel', text_sr: 'Odeća i garderoba', mcc: ['5651'] },
                { value: 'electronics_store', text: 'Electronics Store', text_sr: 'Prodavnica elektronike', mcc: ['5732'] },
                { value: 'home_lifestyle', text: 'Home & Lifestyle Products', text_sr: 'Proizvodi za dom i stil života', mcc: ['5712'] },
                { value: 'beauty_cosmetics', text: 'Beauty & Cosmetics', text_sr: 'Lepota i kozmetika', mcc: ['5977'] },
                { value: 'sporting_goods', text: 'Sporting Goods', text_sr: 'Sportska oprema', mcc: ['5941'] },
                { value: 'digital_products', text: 'Digital Products & Downloads', text_sr: 'Digitalni proizvodi i preuzimanja', mcc: ['5815'] },
                { value: 'software_sales', text: 'Software Sales', text_sr: 'Prodaja softvera', mcc: ['5734'] },
                { value: 'online_education', text: 'Online Education Platform', text_sr: 'Online obrazovna platforma', mcc: ['8299'] },
                { value: 'professional_services', text: 'Professional Services', text_sr: 'Profesionalne usluge', mcc: ['7399'] },
                { value: 'food_beverage_delivery', text: 'Food & Beverage Delivery', text_sr: 'Dostava hrane i pića', mcc: ['5812'] }
            ],

            productTypes: [
                { value: 'physical', text: { en: 'Physical Products', sr: 'Fizički proizvodi' }, textKey: 'ecommerce.shipping.physical', icon: 'box-seam' },
                { value: 'digital', text: { en: 'Digital Products', sr: 'Digitalni proizvodi' }, textKey: 'ecommerce.shipping.digital', icon: 'file-earmark-code' },
                { value: 'service', text: { en: 'Services', sr: 'Usluge' }, textKey: 'ecommerce.shipping.services', icon: 'tools' }
            ],

            paymentMethods: [
                { value: 'credit_card', text: this.$t('ecommerce.payment.creditCard'), icon: 'credit-card' },
                { value: 'bank_transfer', text: this.$t('ecommerce.payment.bankTransfer'), icon: 'bank' },
                { value: 'cash_on_delivery', text: this.$t('ecommerce.payment.cashOnDelivery'), icon: 'cash' }
            ],

            shippingMethods: [
                { value: 'delivery', text: { en: 'Delivery to address', sr: 'Dostava na adresu' }, textKey: 'ecommerce.shipping.delivery', icon: 'truck' },
                { value: 'pickup', text: { en: 'Pickup from shop', sr: 'Preuzimanje u prodavnici' }, textKey: 'ecommerce.shipping.pickupFromShop', icon: 'shop' }
            ]
        }
    },

    async mounted() {
        await this.loadInitialData()
        await this.fetchIntegrationPrice()
        // Pre-populate country from integration config if not already set
        if (!this.formData.companyInfo.country && this.predefinedCountry) {
            this.formData.companyInfo.country = this.predefinedCountry
        }
        // Set currency to RSD for Serbian stores and lock it
        if (this.isSerbian) {
            this.formData.currency = 'RSD'
        }
        // Ensure credit card is selected and provider set for Monri partners
        if (this.isMonriPartner) {
            if (!this.formData.paymentMethods.includes('credit_card')) {
                this.formData.paymentMethods.push('credit_card')
            }
            this.formData.creditCardProvider = 'monri'
        }
        // Pre-load Monri credentials if already configured
        if (this.formData.creditCardProvider === 'monri') {
            await this.loadMonriSettings()
        }
        // Fetch TOS preview if resuming on the documentation step
        if (this.currentStep === 5) {
            this.fetchTosPreview()
        }
    },

    watch: {
        paymentsIntegration: {
            handler() {
                if (this.isMonriPartner) {
                    if (!this.formData.paymentMethods.includes('credit_card')) {
                        this.formData.paymentMethods.push('credit_card')
                    }
                    this.formData.creditCardProvider = 'monri'
                    if (this.isSerbian) {
                        this.formData.creditCardReceivingMethod = 'chip_card'
                    }
                }
            },
            immediate: true
        },
        
        currentStep: {
            handler(newStep) {
                // Ensure credit card is selected when Monri partner reaches payment step
                if (newStep === 4 && this.isMonriPartner && !this.formData.paymentMethods.includes('credit_card')) {
                    this.formData.paymentMethods.push('credit_card')
                }
            }
        },

        'formData.companyInfo.country'(val) {
            if (val === 'RS') {
                this.formData.currency = 'RSD'
            }
        },

        'formData.storeName'(val) {
            if (!this.storeUrlManuallyEdited) {
                this.formData.storeUrl = val
                    .toLowerCase()
                    .replace(/[^a-z0-9\s-]/g, '')
                    .trim()
                    .replace(/[\s]+/g, '-')
                    .replace(/-+/g, '-')
            }
        },

        'formData.storeUrl'(val) {
            this.domainValidationError = null
            this.domainValidationSuccess = false
            clearTimeout(this.domainDebounceTimer)
            if (!val) return
            this.domainDebounceTimer = setTimeout(() => {
                this.validateDomain()
            }, 600)
        }
    },

    computed: {
        shopDomain() {
            return this.$env.get("ECOMMERCE_SHOP_DOMAIN") || 'ptkl.shop'
        },

        fullStoreDomain() {
            const baseDomain = `${this.formData.storeUrl}.${this.shopDomain}`
            return `https://${baseDomain}`
        },

        merchantDescriptionOptionsFormatted() {
            return this.merchantDescriptionOptions.map(option => ({
                value: option.value,
                text: this.formData.language === 'sr' ? option.text_sr : option.text
            }))
        },

        isMonriPartner() {
            return this.paymentsIntegration && this.paymentsIntegration.status === 'active' && this.paymentsIntegration.data && this.paymentsIntegration.data.partner === 'monri'
        },

        isChipcardPartner() {
            return this.paymentsIntegration && this.paymentsIntegration.status === 'active' && this.paymentsIntegration.data && this.paymentsIntegration.data.partner === 'chipcard'
        },

        isAikBank() {
            return this.integration && this.integration.data && this.integration.data.bank === 'aik'
        },

        bankAccountOptions() {
            if (!this.formData.companyInfo.bankAccounts || !Array.isArray(this.formData.companyInfo.bankAccounts)) {
                return []
            }
            return this.formData.companyInfo.bankAccounts.map((bank, index) => ({
                value: index,
                text: `${bank.bank} - ${bank.number}`
            }))
        },

        filteredBankAccounts() {
            if (!this.formData.companyInfo.bankAccounts || !Array.isArray(this.formData.companyInfo.bankAccounts)) {
                return []
            }
            const accounts = this.formData.companyInfo.bankAccounts.map((bank, index) => ({ ...bank, originalIndex: index }))
            if (this.isAikBank) {
                return accounts.filter(bank => bank.number && bank.number.startsWith('105'))
            }
            return accounts
        },

        isSerbian() {
            const integrationCountry = this.integration && this.integration.data && this.integration.data.country
            return integrationCountry === 'RS' || this.formData.companyInfo.country === 'RS'
        },

        predefinedCountry() {
            return this.integration && this.integration.data && this.integration.data.country
        },

        steps() {
            return [
                { label: this.$t('ecommerce.steps.welcome') },
                { label: this.$t('ecommerce.steps.companyInfo') },
                { label: this.$t('ecommerce.steps.store') },
                { label: this.$t('ecommerce.steps.shipping') },
                { label: this.$t('ecommerce.steps.payment') },
                { label: this.$t('ecommerce.steps.documentation') },
                { label: this.$t('ecommerce.steps.summary') }
            ]
        },
        
        canProceed() {
            switch (this.currentStep) {
                case 0:
                    return true
                case 1:
                    // Company info — Serbian requires full form, others only name + email + country
                    if (!this.formData.companyInfo.country) return false
                    if (this.isSerbian) {
                        return !!(this.formData.companyInfo.pib &&
                               this.formData.companyInfo.pib.length === 9 &&
                               !this.pibError &&
                               !this.fetchingCompanyData &&
                               this.formData.companyInfo.legalName &&
                               this.formData.companyInfo.companyName &&
                               this.formData.companyInfo.companyAddress &&
                               this.formData.companyInfo.companyCity &&
                               this.formData.companyInfo.companyPostalCode &&
                               this.formData.companyInfo.companyPhone &&
                               this.formData.companyInfo.companyEmail &&
                               this.formData.companyInfo.pdv !== null)
                    }
                    return !!(this.formData.companyInfo.companyName && this.formData.companyInfo.companyEmail && this.formData.companyInfo.pdv !== null)
                case 2:
                    // Store settings
                    return !!(this.formData.storeName && this.formData.storeUrl && this.formData.currency && this.formData.language && this.formData.merchantDescription && !this.domainValidationError && !this.validatingDomain)
                case 3:
                    // Shipping
                    if (!this.formData.productType) return false
                    if (this.formData.productType === 'physical') {
                        return this.formData.shippingMethods.length > 0
                    }
                    return true
                case 4:
                    // Payment
                    if (this.formData.paymentMethods.length === 0) return false
                    if (this.formData.paymentMethods.includes('credit_card')) {
                        if (!this.formData.creditCardProvider) return false
                        if (this.formData.creditCardProvider === 'monri' && !this.isMonriPartner && !this.formData.monriSetupLater) {
                            if (!this.formData.monriMerchantKey || !this.formData.monriAuthenticityToken) return false
                        }
                        if (!this.formData.creditCardReceivingMethod && (this.isChipcardPartner || this.isAikBank)) return false
                        if (this.formData.creditCardReceivingMethod === 'bank_transfer' &&
                            this.formData.companyInfo.bankAccounts && this.formData.companyInfo.bankAccounts.length > 0) {
                            return this.formData.companyInfo.selectedBankAccount !== null
                        }
                    }
                    return true
                case 5:
                    // Documentation
                    return true
                case 6:
                    // Summary
                    return true
                default:
                    return true
            }
        }
    },

    i18n: {
        messages: {
            en_US: {
                back: 'Back',
                continue: 'Continue',
                finish: 'Finish Setup',
                saving: 'Saving...',
                cancel: 'Cancel',
                ecommerce: {
                    loading: 'Loading your store settings...',
                    needHelp: 'You need help or you are stuck?',
                    steps: {
                        welcome: 'Welcome',
                        store: 'Settings',
                        companyInfo: 'Company Info',
                        domains: 'Your Domains',
                        shipping: 'Category',
                        payment: 'Payment',
                        documentation: 'Documentation',
                        summary: 'Summary'
                    },
                    welcome: {
                        title: 'Welcome to E-commerce Integration',
                        description: 'Set up your online store in just a few steps. This integration will help you manage products, orders, and customers efficiently.',
                        feature1: 'Choose your webshop name, get a free domain',
                        feature2: 'Manage products, prices, and inventory in one place',
                        feature3: 'Track orders, deliveries, and payments in real-time',
                        feature4: 'Analyze sales and customer data',
                        feature5: 'Accept online payments',
                        pricingTitle: 'Monthly Subscription',
                        perMonth: 'per month',
                        confirmationTitle: 'Legal Confirmation',
                        confirmationText: 'I, under full material and criminal responsibility, confirm that the information is accurate and that I am the legal representative of the company.',
                        confirmButton: 'I Confirm'
                    },
                    store: {
                        title: 'Store Configuration',
                        description: 'Let\'s start by setting up your basic store information',
                        storeName: 'Store Name',
                        storeNamePlaceholder: 'My Awesome Shop',
                        storeUrl: 'Webshop URL',
                        storeUrlPlaceholder: 'my-store',
                        storeUrlHint: 'Your store will be available at: {domain}',
                        domainUnavailable: 'This domain is already taken',
                        logo: 'Store Logo',
                        logoPlaceholder: 'Choose a file or drop it here...',
                        logoDropPlaceholder: 'Drop file here...',
                        logoHint: 'Upload your store logo (recommended size: 200x200px, max 500KB)',
                        logoFallbackHint: 'Your store name will be used as the default logo',
                        logoUploaded: 'Logo uploaded successfully',
                        logoSizeError: 'Logo file is too large. Maximum allowed size is 500KB.',
                        currency: 'Default Currency',
                        language: 'Main Language',
                        merchantDescription: 'Description'
                    },
                    companyInfo: {
                        title: 'Company Information',
                        description: 'Provide your company details for legal compliance',
                        country: 'Country',
                        countryPlaceholder: 'Select country...',
                        pib: 'PIB (Tax ID)',
                        pibPlaceholder: '123456789',
                        pibHint: 'Enter 9 digits to auto-fill company information',
                        pibLengthError: 'PIB must be exactly 9 digits',
                        pibFetchError: 'Could not fetch company data. Please enter manually.',
                        legalName: 'Legal Name',
                        legalNamePlaceholder: 'My Company d.o.o.',
                        companyName: 'Company Name',
                        companyNamePlaceholder: 'My Company',
                        companyNameHint: 'Display name for your store (max 40 characters)',
                        companyAddress: 'Address',
                        companyAddressPlaceholder: 'Street name and number',
                        companyCity: 'City',
                        companyCityPlaceholder: 'Belgrade',
                        companyPostalCode: 'Postal Code',
                        companyPostalCodePlaceholder: '11000',
                        companyPhone: 'Phone',
                        companyPhonePlaceholder: '+381 11 123 4567',
                        companyEmail: 'Email Address',
                        companyEmailPlaceholder: 'info@company.com',
                        legalRepresentative: 'Legal Representative',
                        legalRepresentativePlaceholder: 'Full name of legal representative',
                        mb: 'MB (Registration Number)',
                        mbPlaceholder: 'Auto-filled from PIB',
                        activityCode: 'Activity Code',
                        activityCodePlaceholder: 'Auto-filled from PIB',
                        bankAccount: 'Bank Account for Credit Card Deposits',
                        selectBankAccount: 'Select a bank account',
                        bankAccountHint: 'Choose the bank account where credit card payments will be deposited',
                        pdvQuestion: 'Are you registered for VAT?',
                        pdvYes: 'Yes',
                        pdvNo: 'No'
                    },
                    domains: {
                        title: 'Your Domains',
                        description: 'Add your own custom domains for your store (optional)',
                        addDomain: 'Add Custom Domain',
                        domainPlaceholder: 'https://www.your-domain.com',
                        domainHint: 'Enter your full domain URL including https://',
                        yourDomains: 'Your Custom Domains',
                        setupCustomDomain: 'Do you want to set up your own domain?',
                        setupGuide: 'How to setup my custom domain?',
                        add: 'Add',
                        placeholder: 'example.com',
                        hint: 'Enter domain without http:// or https://'
                    },
                    payment: {
                        title: 'Payment Methods',
                        description: 'Configure how customers will pay for their orders',
                        methods: 'Payment Methods',
                        monriPreselectedInfo: 'Setting up ecommerce as Monri partner this option is preselected by default',
                        monriProvider: 'Payment provider: Monri',
                        monriAutoOnboarding: 'Monri onboarding will be automatically initiated after completing this setup',
                        choosePaymentType: 'Choose payment methods',
                        paymentCard: 'Cashless payment',
                        required: 'Required',
                        chooseProvider: 'Choose payment provider',
                        paymentProcessor: 'Payment processor',
                        merchantKey: 'Merchant Key',
                        merchantKeyPlaceholder: 'Enter your Merchant Key',
                        authenticityToken: 'Authenticity Token',
                        authenticityTokenPlaceholder: 'Enter your Authenticity Token',
                        monriCredentials: 'Monri Settings',
                        monriPartnerCredentialsInfo: 'Credentials are managed by your Monri partnership agreement.',
                        chooseCreditCardReceiving: 'How do you want to receive credit card payments?',
                        creditCardReceivingDescription: 'Choose how credit card payments will be transferred to you',
                        creditCard: 'Credit/Debit Card',
                        bankTransfer: 'Bank Transfer',
                        cashOnDelivery: 'Cash on Delivery',
                        bank: 'Bank',
                        whatIsChipcard: 'What is Chipcard?',
                        chipcardModalTitle: 'About Chipcard',
                        chipcardInfo1: 'Chip Card is one of the leading payment transaction processors operating in the Republic of Serbia and the region since 2004, representing a reliable and stable partner to its clients – banks, merchants, and other financial and non-financial organizations.',
                        chipcardInfoBusinessLines: 'Chip Card\'s business consists of two business lines:',
                        chipcardLine1: 'Payment transaction processing services for banks and financial institutions',
                        chipcardLine2: 'Electronic money institution services, e-Commerce and POS integration, for merchants',
                        chipcardInfo2: 'Through Payten d.o.o. Belgrade, as the majority shareholder, Chip Card is connected to companies within the Payten group, which operates within the broader Asseco group.',
                        bankAccountForCreditCard: 'Select the bank account where credit card payments will be deposited',
                        noBankAccount: 'You have no open account. Are you interested in opening one?',
                        bankAccountActive: 'Active',
                        bankAccountInactive: 'Inactive',
                        bankTransferInstruction: 'Payment Instructions (PDF)',
                        bankTransferInstructionPlaceholder: 'Choose PDF file...',
                        bankTransferInstructionHint: 'Upload a PDF with bank account details and payment instructions for customers',
                        bankTransferInstructionUploadPrompt: 'Click to upload payment instructions PDF',
                        bankTransferInstructionUploaded: 'Payment instructions PDF uploaded',
                        setupLater: 'I will set this up later'
                    },
                    shipping: {
                        title: 'Category',
                        description: 'Choose your store category',
                        productType: 'Type of Products',
                        methods: 'Delivery Methods',
                        standard: 'Standard Shipping',
                        express: 'Express Shipping',
                        pickup: 'Store Pickup',
                        physical: 'Physical Products',
                        digital: 'Digital Products',
                        services: 'Services',
                        delivery: 'Delivery to address',
                        pickupFromShop: 'Pickup from shop'
                    },
                    monri: {
                        description: 'Configure your Monri payment integration',
                        integrationInfo: 'Your Monri integration is active and will be used for processing payments'
                    },
                    documentation: {
                        title: 'Terms of Service',
                        description: 'Review your auto-generated terms of service or upload your own.',
                        useAutoGenerated: 'Use auto-generated documents (recommended)',
                        uploadOwn: 'Upload my own documents',
                        autoGeneratedInfo: 'Your Terms of Service are automatically generated based on your store settings.',
                        termsOfService: 'Terms of Service',
                        uploadPlaceholder: 'Choose PDF file...',
                        uploadPrompt: 'Click here or drag and drop file to upload',
                        uploadHint: 'PDF format only',
                        fileUploaded: 'Document uploaded successfully',
                        customFileUploaded: 'Custom document uploaded',
                        autoGeneratedPreview: 'Document will be automatically generated based on your store configuration',
                        tosPreviewInfo: 'This is how your Terms of Service will appear to your customers.',
                        tosPreviewError: 'Could not load Terms of Service preview.',
                        uploadCustom: 'Or upload your own custom Terms of Service'
                    },
                    summary: {
                        title: 'All Set!',
                        description: 'Review your configuration and complete the setup',
                        storeInfo: 'Store Information',
                        companyInfo: 'Company Information',
                        domainsInfo: 'Custom Domains',
                        paymentInfo: 'Payment Methods',
                        paymentMethod: 'Payment Method',
                        receivingMethod: 'Receiving Method',
                        selectedBank: 'Selected Bank',
                        shippingInfo: 'Category & Shipping',
                        shippingMethod: 'Shipping Method',
                        autoGenerated: 'Auto-generated',
                        noShippingRequired: 'No physical shipping required'
                    },
                    chipcard: {
                        title: 'Almost Done!',
                        understood: 'I Understand',
                        prepareTitle: 'Prepare for the next step',
                        prepareDescription: 'In order to prepare for the next phase of onboarding you will be contacted by Chipcard. Please prepare the following documentation:',
                        doc1: 'A valid personal document (ID card or passport).',
                        doc2: 'If the ID card has a chip or does not contain address information, prepare a scanned ID card or one of the following documents: property tax determination, vehicle registration, phone bill, utility bill, etc.',
                        doc3: 'Please prepare an extract from the APR register not older than three months.',
                        doc4: 'In case of establishing a contractual relationship through a proxy (prokurist), it is necessary to come in person to the Chip Card address.'
                    }
                },
                complete: {
                    title: 'Setup Complete!',
                    description: 'Your online store has been set up successfully. We are preparing everything for you.'
                }
            },
            sr: {
                back: 'Nazad',
                continue: 'Nastavi',
                finish: 'Završi podešavanje',
                saving: 'Čuvam...',
                cancel: 'Otkaži',
                ecommerce: {
                    loading: 'Učitavam podešavanja prodavnice...',
                    needHelp: 'Trebaš pomoć ili si zaglavio/la?',
                    steps: {
                        welcome: 'Dobrodošli',
                        store: 'Podešavanja',
                        companyInfo: 'Informacije o kompaniji',
                        domains: 'Vaši domeni',
                        shipping: 'Kategorija',
                        payment: 'Plaćanje',
                        documentation: 'Dokumentacija',
                        summary: 'Pregled'
                    },
                    welcome: {
                        title: 'Tvoj webshop u par koraka',
                        description: 'Popunite ovu brzu formu u par koraka i dobijate webshop spreman za rad',
                        feature1: 'Izaberite ime svog webshopa, domen dobijate besplatno',
                        feature2: 'Upravljajte proizvodima, cenama i zalihama na jednom mestu',
                        feature3: 'Pratite porudžbine, dostave i plaćanja u realnom vremenu',
                        feature4: 'Analizirajte prodaju i podatke o kupcima',
                        feature5: 'Prihvat online plaćanja',
                        pricingTitle: 'Mesečna pretplata',
                        perMonth: 'mesečno',
                        confirmationTitle: 'Pravna potvrda',
                        confirmationText: 'Ovim potvrđujem pod punom zakonskom odgovornošću da su navedeni podaci istiniti kao ovlašćeni zastupnik privrednog subjekta',
                        confirmButton: 'Potvrđujem'
                    },
                    store: {
                        title: 'Podešavanje prodavnice',
                        description: 'Počnimo sa podešavanjem osnovnih informacija o vašoj prodavnici',
                        storeName: 'Naziv prodavnice',
                        storeNamePlaceholder: 'Moja prodavnica',
                        storeUrl: 'Webshop URL',
                        storeUrlPlaceholder: 'moja-prodavnica',
                        storeUrlHint: 'Vaša prodavnica će biti dostupna na: {domain}',
                        domainUnavailable: 'Ovaj domen je već zauzet',
                        logo: 'Logo prodavnice',
                        logoPlaceholder: 'Izaberite fajl ili ga prevucite ovde...',
                        logoDropPlaceholder: 'Prevucite fajl ovde...',
                        logoHint: 'Otpremite logo vaše prodavnice (preporučena veličina: 200x200px, maks. 500KB)',
                        logoFallbackHint: 'Naziv vaše prodavnice će biti korišćen kao podrazumevani logo',
                        logoUploaded: 'Logo je uspešno otpremljen',
                        logoSizeError: 'Fajl logotipa je prevelik. Maksimalna dozvoljena veličina je 500KB.',
                        currency: 'Osnovna valuta',
                        language: 'Glavni jezik',
                        merchantDescription: 'Opis'
                    },
                    companyInfo: {
                        title: 'Informacije o kompaniji',
                        description: 'Unesite podatke o vašoj kompaniji za pravnu usaglašenost',
                        country: 'Država',
                        countryPlaceholder: 'Izaberite državu...',
                        pib: 'PIB',
                        pibPlaceholder: '123456789',
                        pibHint: 'Unesite 9 cifara za automatsko popunjavanje podataka',
                        pibLengthError: 'PIB mora imati tačno 9 cifara',
                        pibFetchError: 'Nije moguće preuzeti podatke o kompaniji. Molimo unesite ručno.',
                        legalName: 'Registrovani naziv',
                        legalNamePlaceholder: 'Moja kompanija d.o.o.',
                        companyName: 'Naziv kompanije',
                        companyNamePlaceholder: 'Moja kompanija',
                        companyNameHint: 'Naziv za prikaz u prodavnici (maks. 40 karaktera)',
                        companyAddress: 'Adresa',
                        companyAddressPlaceholder: 'Naziv ulice i broj',
                        companyCity: 'Grad',
                        companyCityPlaceholder: 'Beograd',
                        companyPostalCode: 'Poštanski broj',
                        companyPostalCodePlaceholder: '11000',
                        companyPhone: 'Telefon',
                        companyPhonePlaceholder: '+381 11 123 4567',
                        companyEmail: 'Email adresa',
                        companyEmailPlaceholder: 'info@kompanija.com',
                        legalRepresentative: 'Zakonski zastupnik',
                        legalRepresentativePlaceholder: 'Puno ime zakonskog zastupnika',
                        mb: 'MB (Matični broj)',
                        mbPlaceholder: 'Automatski popunjeno iz PIB-a',
                        activityCode: 'Šifra delatnosti',
                        activityCodePlaceholder: 'Automatski popunjeno iz PIB-a',
                        bankAccount: 'Bankovni račun za uplatu karticom',
                        selectBankAccount: 'Izaberite bankovni račun',
                        bankAccountHint: 'Izaberite bankovni račun na koji će biti uplaćena plaćanja karticom',
                        pdvQuestion: 'Da li ste u sistemu PDV-a?',
                        pdvYes: 'Da',
                        pdvNo: 'Ne'
                    },
                    domains: {
                        title: 'Vaši domeni',
                        description: 'Dodajte vaše prilagođene domene za prodavnicu (opciono)',
                        addDomain: 'Dodaj prilagođeni domen',
                        domainPlaceholder: 'https://www.vas-domen.com',
                        domainHint: 'Unesite punu URL adresu domena uključujući https://',
                        yourDomains: 'Vaši prilagođeni domeni',
                        setupCustomDomain: 'Da li želite da podesite sopstveni domen?',
                        setupGuide: 'Kako da podesim sopstveni domen?',
                        add: 'Dodaj',
                        placeholder: 'primer.com',
                        hint: 'Unesite domen bez http:// ili https://'
                    },
                    payment: {
                        title: 'Načini plaćanja',
                        description: 'Podesite kako će kupci plaćati svoje narudžbine',
                        methods: 'Načini plaćanja',
                        monriPreselectedInfo: 'Podešavanje e-commerce kao Monri partner ova opcija je unapred izabrana',
                        monriProvider: 'Provajder plaćanja: Monri',
                        monriAutoOnboarding: 'Monri onboarding će biti automatski pokrenut nakon završetka ovog podešavanja',
                        choosePaymentType: 'Izaberite načine plaćanja',
                        paymentCard: 'Bezgotovinsko plaćanje',
                        required: 'Obavezno',
                        chooseProvider: 'Izaberite pružaoca plaćanja',
                        paymentProcessor: 'Procesor platnih transakcija',
                        merchantKey: 'Merchant Key',
                        merchantKeyPlaceholder: 'Unesite vaš Merchant Key',
                        authenticityToken: 'Authenticity Token',
                        authenticityTokenPlaceholder: 'Unesite vaš Authenticity Token',
                        monriCredentials: 'Monri podešavanja',
                        monriPartnerCredentialsInfo: 'Akredentivi su definisani vašim Monri partnerskim ugovorom.',
                        chooseCreditCardReceiving: 'Kako želite da primate plaćanja karticom?',
                        creditCardReceivingDescription: 'Izaberite kako će vam biti preneta plaćanja karticom',
                        creditCard: 'Kreditna/debitna kartica',
                        bankTransfer: 'Uplata na račun',
                        cashOnDelivery: 'Plaćanje pouzećem',
                        bank: 'Banka',
                        whatIsChipcard: 'Šta je Chipcard?',
                        chipcardModalTitle: 'O Chipcard-u',
                        chipcardInfo1: 'Chip Card je jedan od vodećih procesora platnih transakcija koji posluje na teritoriji Republike Srbije i u regionu od 2004. godine i predstavlja pouzdanog i stabilnog partnera svojim klijentima – bankama, trgovcima i drugim finansijskim i nefinansijskim organizacijama.',
                        chipcardInfoBusinessLines: 'Poslovanje Chip Card-a se sastoji od dve poslovne linije:',
                        chipcardLine1: 'Poslovi procesiranja platnih transakcija namenjeni bankama i finansijskim institucijama',
                        chipcardLine2: 'Poslovi Institucije elektronskog novca, e-Commerce i POS integracija, namenjeni trgovcima',
                        chipcardInfo2: 'Preko Payten d.o.o. Beograd, kao većinskog akcionara, Chip Card je povezan sa kompanijama u sastavu Payten grupe koja posluje u okviru šire Asseco grupacije.',
                        bankAccountForCreditCard: 'Izaberite bankovni račun na koji će biti uplaćena plaćanja karticom',
                        noBankAccount: 'Nemate otvoren račun, da li ste zainteresovani da otvorite račun?',
                        bankAccountActive: 'Aktivan',
                        bankAccountInactive: 'Neaktivan',
                        bankTransferInstruction: 'Uputstvo za plaćanje (PDF)',
                        bankTransferInstructionPlaceholder: 'Izaberite PDF fajl...',
                        bankTransferInstructionHint: 'Otpremite PDF sa detaljima bankovnog računa i uputstvima za plaćanje za kupce',
                        bankTransferInstructionUploadPrompt: 'Kliknite da otpremite PDF sa uputstvima za plaćanje',
                        bankTransferInstructionUploaded: 'PDF sa uputstvima za plaćanje je otpremljen',
                        setupLater: 'Podešuću ovo kasnije'
                    },
                    shipping: {
                        title: 'Kategorija',
                        description: 'Izaberite kategoriju vaše prodavnice',
                        productType: 'Tip proizvoda',
                        methods: 'Načini dostave',
                        standard: 'Standardna dostava',
                        express: 'Brza dostava',
                        pickup: 'Preuzimanje u prodavnici',
                        physical: 'Fizički proizvodi',
                        digital: 'Digitalni proizvodi',
                        services: 'Usluge',
                        delivery: 'Dostava na adresu',
                        pickupFromShop: 'Preuzimanje u prodavnici'
                    },
                    monri: {
                        description: 'Konfigurišite vašu Monri integraciju za plaćanje',
                        integrationInfo: 'Vaša Monri integracija je aktivna i biće korišćena za obradu plaćanja'
                    },
                    documentation: {
                        title: 'Uslovi korišćenja',
                        description: 'Pregledajte automatski generisane uslove korišćenja ili otpremite svoje.',
                        useAutoGenerated: 'Koristi automatski generisane dokumente (preporučeno)',
                        uploadOwn: 'Otpremi moje dokumente',
                        autoGeneratedInfo: 'Uslovi korišćenja su automatski generisani na osnovu podešavanja vaše prodavnice.',
                        termsOfService: 'Uslovi korišćenja',
                        uploadPlaceholder: 'Izaberite PDF fajl...',
                        uploadPrompt: 'Kliknite ovde ili prevucite fajl za otpremanje',
                        uploadHint: 'Samo PDF format',
                        fileUploaded: 'Dokument je uspešno otpremljen',
                        customFileUploaded: 'Prilagođeni dokument otpremljen',
                        autoGeneratedPreview: 'Dokument će biti automatski generisan na osnovu konfiguracije vaše prodavnice',
                        tosPreviewInfo: 'Ovako će vaši uslovi korišćenja izgledati za vaše kupce.',
                        tosPreviewError: 'Nije moguće učitati pregled uslova korišćenja.',
                        uploadCustom: 'Ili otpremite vaše prilagođene uslove korišćenja'
                    },
                    summary: {
                        title: 'Sve je spremno!',
                        description: 'Pregledajte vašu konfiguraciju i završite podešavanje',
                        storeInfo: 'Informacije o prodavnici',
                        companyInfo: 'Informacije o kompaniji',
                        domainsInfo: 'Prilagođeni domeni',
                        paymentInfo: 'Načini plaćanja',
                        paymentMethod: 'Način plaćanja',
                        receivingMethod: 'Način prijema',
                        selectedBank: 'Izabrana banka',
                        shippingInfo: 'Kategorija i dostava',
                        shippingMethod: 'Način dostave',
                        autoGenerated: 'Automatski generisano',
                        noShippingRequired: 'Fizička dostava nije potrebna'
                    },
                    chipcard: {
                        title: 'Još samo malo!',
                        understood: 'Razumem',
                        prepareTitle: 'Pripremite se za sledeći korak',
                        prepareDescription: 'Kako biste se pripremili za sledeću fazu ugovaranja, kontaktiraće vas Chipcard. Molimo vas da pripremite sledeću dokumentaciju:',
                        doc1: 'Validan lični dokument (lična karta ili pasoš).',
                        doc2: 'U slučaju da je lična karta sa čipom ili da dokument ne sadrži podatke o adresi, pripremiti očitanu ličnu kartu ili neki od sledećih dokumenata: rešenje o utvrđivanju poreza na imovinu, saobraćajna dozvola, račun za telefon, komunalne usluge i sl.',
                        doc3: 'Molimo vas da pripremite Izvod iz registra APR-a ne stariji od tri meseca.',
                        doc4: 'U slučaju uspostavljanja ugovornog odnosa preko prokuriste potrebno je lično doći na adresu Chip Card-a.'
                    }
                },
                complete: {
                    title: 'Podešavanje završeno!',
                    description: 'Vaša online prodavnica je uspešno podešena. Pripremamo sve za vas.'
                }
            }
        }
    },

    methods: {
        openSupportDialog: function() {
            this.$root.$emit('open-support-ticket-dialog', {
                subject: 'Problems with ecommerce onboarding'
            })
        },

        getLanguageText(langValue) {
            const lang = this.languageOptions.find(l => l.value === langValue)
            return lang ? lang.text : langValue
        },

        getMerchantDescriptionText() {
            const merchant = this.merchantDescriptionOptions.find(m => m.value === this.formData.merchantDescription)
            if (!merchant) return this.formData.merchantDescription
            return this.formData.language === 'sr' ? merchant.text_sr : merchant.text
        },

        async fetchIntegrationPrice() {
            this.loadingPrice = true
            try {
                const user = this.$store.getters.getUser
                const billingAddress = user && user.settings && user.settings.billing_address
                const country = billingAddress ? billingAddress.country : 'RS'
                this.priceHelper = new Price(country, this.$store.getters.getLocale)
                const currency = this.priceHelper.getCurrency()

                const resp = await this.$transactions.getPricesForProduct('integration', 'protokol-ecommerce', currency)
                const total = resp.data.total_amount

                if (currency === 'RSD') {
                    this.integrationPrice = { formatted: `${Math.round(total).toLocaleString('sr-RS')} RSD` }
                } else {
                    this.integrationPrice = { formatted: `$${total.toFixed(2)}` }
                }
            } catch (err) {
                console.error('Failed to fetch integration price:', err)
            } finally {
                this.loadingPrice = false
            }
        },

        async loadInitialData() {
            try {
                this.loadingSettings = true

                // Fetch integrations
                await this.$store.dispatch('fetchIntegrations')
                const integrations = this.$store.getters.getIntegrations
                this.integration = integrations.find(i => i.id === 'protokol-ecommerce')
                this.paymentsIntegration = integrations.find(i => i.id === 'protokol-payments')

                // Set default language based on country
                if (this.isSerbian) {
                    this.formData.language = 'sr'
                }

                // Preselect credit card if Monri partner
                if (this.isMonriPartner && !this.formData.paymentMethods.includes('credit_card')) {
                    this.formData.paymentMethods.push('credit_card')
                }
                this.paymentsIntegration = integrations.find(i => i.id === 'protokol-payments')

                // Fetch ecommerce settings
                const { data } = await this.$integrationApi.get('/ecomm/user')
                this.ecommSettings = data

                // Restore step progress from cache (but not field values — API wins for those)
                const cachedData = this.getCachedData()
                if (cachedData) {
                    this.currentStep = cachedData.currentStep || 0
                }

                // Always apply API data last so DB values always take priority
                if (this.ecommSettings) {
                    if (this.ecommSettings.base_domain) {
                        // Extract only the subdomain part (first part before the first dot)
                        const domainParts = this.ecommSettings.base_domain.split('.')
                        this.formData.storeUrl = domainParts[0]
                        this.storeUrlManuallyEdited = true
                    }
                    if (this.ecommSettings.general && this.ecommSettings.general.store_name) {
                        this.formData.storeName = this.ecommSettings.general.store_name
                    }
                    if (this.ecommSettings.custom_domains && this.ecommSettings.custom_domains.length > 0) {
                        this.formData.customDomains = [...this.ecommSettings.custom_domains]
                    }
                    if (this.ecommSettings.settings && this.ecommSettings.settings.dev && this.ecommSettings.settings.dev.header && this.ecommSettings.settings.dev.header.logo && this.ecommSettings.settings.dev.header.logo.src) {
                        this.formData.logoBase64 = this.ecommSettings.settings.dev.header.logo.src
                    }
                    if (this.ecommSettings.settings && this.ecommSettings.settings.dev && this.ecommSettings.settings.dev.price && this.ecommSettings.settings.dev.price.default_currency && this.ecommSettings.settings.dev.price.default_currency.id) {
                        this.formData.currency = this.ecommSettings.settings.dev.price.default_currency.id.toUpperCase()
                    }
                    if (this.ecommSettings.settings && this.ecommSettings.settings.dev && this.ecommSettings.settings.dev.cart && this.ecommSettings.settings.dev.cart.payment_methods && this.ecommSettings.settings.dev.cart.payment_methods.methods && this.ecommSettings.settings.dev.cart.payment_methods.methods.length > 0) {
                        this.formData.paymentMethods = this.ecommSettings.settings.dev.cart.payment_methods.methods.map(m =>
                            typeof m === 'string' ? m : m.id
                        )
                    }
                    if (this.ecommSettings.settings && this.ecommSettings.settings.dev && this.ecommSettings.settings.dev.cart && this.ecommSettings.settings.dev.cart.shipping_methods && this.ecommSettings.settings.dev.cart.shipping_methods.methods && this.ecommSettings.settings.dev.cart.shipping_methods.methods.length > 0) {
                        this.formData.shippingMethods = this.ecommSettings.settings.dev.cart.shipping_methods.methods.map(m =>
                            typeof m === 'string' ? m : m.id
                        )
                    }
                    if (this.ecommSettings.company) {
                        const c = this.ecommSettings.company
                        this.formData.companyInfo.country = c.country || ''
                        this.formData.companyInfo.pib = c.vat_number || ''
                        this.formData.companyInfo.legalName = c.legal_name || ''
                        this.formData.companyInfo.companyName = c.company_name || ''
                        this.formData.companyInfo.companyAddress = c.address || ''
                        this.formData.companyInfo.companyCity = c.city || ''
                        this.formData.companyInfo.companyPostalCode = c.zip_code || ''
                        this.formData.companyInfo.companyPhone = c.phone ? c.phone.replace('+381', '') : ''
                        this.formData.companyInfo.companyEmail = c.email || ''
                        this.formData.companyInfo.legalRepresentative = c.legal_representative || ''
                        if (c.additional_info) {
                            this.formData.companyInfo.mb = c.additional_info.registration_number || ''
                            this.formData.companyInfo.activityCode = c.additional_info.activity_code || ''
                            this.formData.companyInfo.pdv = c.additional_info.pdv != null ? c.additional_info.pdv : null
                        }
                    }
                    if (this.ecommSettings.merchant_description) {
                        const matchedOption = this.merchantDescriptionOptions.find(
                            opt => opt.text === this.ecommSettings.merchant_description || opt.value === this.ecommSettings.merchant_description
                        )
                        this.formData.merchantDescription = matchedOption ? matchedOption.value : ''
                    }
                    if (this.ecommSettings.product_types && this.ecommSettings.product_types.length > 0) {
                        this.formData.productType = this.ecommSettings.product_types[0]
                    }
                    // Always enforce RSD for Serbian stores regardless of stored settings
                    if (this.isSerbian) {
                        this.formData.currency = 'RSD'
                    }
                }
            } catch (err) {
                console.error('Failed to load initial data:', err)
                // Try to restore from cache even if API fails
                const cachedData = this.getCachedData()
                if (cachedData) {
                    this.currentStep = cachedData.currentStep || 0
                    this.formData = { ...this.formData, ...cachedData.formData }
                }
            } finally {
                this.loadingSettings = false
            }
        },

        nextStep() {
            if (this.canProceed && this.currentStep < this.steps.length - 1) {
                // Show legal confirmation alert when leaving company info step for Serbian stores
                if (this.currentStep === 1 && this.isSerbian) {
                    this.showLegalModal = true
                    return
                }
                const leavingStep = this.currentStep
                this.currentStep++
                // Ensure credit card is selected when entering payment step if Monri partner
                if (this.currentStep === 4 && this.isMonriPartner && !this.formData.paymentMethods.includes('credit_card')) {
                    this.formData.paymentMethods.push('credit_card')
                }
                this.saveToCache()
                this.saveStepData(leavingStep)
                if (this.currentStep === 5) {
                    this.fetchTosPreview()
                }
            }
        },

        handleLegalConfirmation() {
            this.showLegalModal = false
            const leavingStep = this.currentStep
            this.currentStep++
            this.saveToCache()
            this.saveStepData(leavingStep)
            if (this.currentStep === 5) {
                this.fetchTosPreview()
            }
        },

        togglePaymentMethod(method) {
            const index = this.formData.paymentMethods.indexOf(method)
            if (index > -1) {
                // Remove method
                this.formData.paymentMethods.splice(index, 1)
                // Clear related data when removing credit card
                if (method === 'credit_card') {
                    this.formData.creditCardReceivingMethod = null
                    this.formData.companyInfo.selectedBankAccount = null
                }
            } else {
                // Add method
                this.formData.paymentMethods.push(method)
            }
            this.saveToCache()
        },

        toggleShippingMethod(method) {
            const index = this.formData.shippingMethods.indexOf(method)
            if (index > -1) {
                // Remove method
                this.formData.shippingMethods.splice(index, 1)
            } else {
                // Add method
                this.formData.shippingMethods.push(method)
            }
            this.saveToCache()
        },

        selectBankTransferReceiving() {
            this.formData.creditCardReceivingMethod = 'bank_transfer'
            this.saveToCache()
        },

        onLogoChange(event) {
            const file = event.target.files[0]
            if (!file) {
                this.formData.logoBase64 = ''
                this.logoSizeError = false
                return
            }

            if (file.size > 512000) {
                this.formData.logoBase64 = ''
                this.logoFile = null
                this.logoSizeError = true
                return
            }

            this.logoSizeError = false
            const reader = new FileReader()
            reader.onload = (e) => {
                this.formData.logoBase64 = e.target.result
                this.saveToCache()
            }
            reader.readAsDataURL(file)
        },

        onPibInput(value) {
            // Only allow numbers
            const numericValue = value.replace(/\D/g, '')
            this.formData.companyInfo.pib = numericValue
            
            // Reset error
            this.pibError = null
            
            // Validate length
            if (numericValue.length > 0 && numericValue.length < 9) {
                this.pibError = this.$t('ecommerce.companyInfo.pibLengthError')
            } else if (numericValue.length === 9) {
                // Fetch company data
                this.fetchCompanyData(numericValue)
            }
        },

        async fetchCompanyData(pib) {
            this.fetchingCompanyData = true
            this.pibError = null
            
            try {
                const { data } = await this.$http.get(`/luma/integrations/karadjordje/v1/services/nbs/search?vatNumber=${pib}`)
                
                if (data) {
                    // Auto-fill company information
                    this.formData.companyInfo.legalName = data.name || ''
                    this.formData.companyInfo.companyName = (data.name || '').trim().slice(-40).trim()
                    this.formData.companyInfo.companyAddress = data.address || ''
                    this.formData.companyInfo.companyCity = data.place || ''
                    this.formData.companyInfo.mb = data.mb || ''
                    this.formData.companyInfo.activityCode = data.activity_code || ''
                    
                    // Store bank accounts from NBS response
                    if (data.banks && Array.isArray(data.banks)) {
                        this.formData.companyInfo.bankAccounts = data.banks
                    } else {
                        this.formData.companyInfo.bankAccounts = []
                    }
                    
                    // Auto-select first bank if only one exists
                    if (this.formData.companyInfo.bankAccounts.length === 1) {
                        this.formData.companyInfo.selectedBankAccount = 0
                    }
                    
                    this.saveToCache()
                }
            } catch (error) {
                console.error('Error fetching company data:', error)
                this.pibError = this.$t('ecommerce.companyInfo.pibFetchError')
            } finally {
                this.fetchingCompanyData = false
            }
        },

        onBankInstructionChange(event) {
            const file = event.target.files[0]
            if (!file) {
                this.formData.bankTransferInstructionPdf = ''
                return
            }

            if (file.type === 'application/pdf') {
                const reader = new FileReader()
                reader.onload = (e) => {
                    this.formData.bankTransferInstructionPdf = e.target.result
                    this.saveToCache()
                }
                reader.readAsDataURL(file)
            }
        },

        onTermsOfServiceChange(event) {
            const file = event.target.files[0]
            if (!file) {
                this.formData.termsOfService = ''
                return
            }

            if (file.type === 'application/pdf') {
                const reader = new FileReader()
                reader.onload = (e) => {
                    this.formData.termsOfService = e.target.result
                    this.formData.useAutoGeneratedDocs = false
                    this.saveToCache()
                }
                reader.readAsDataURL(file)
            }
        },

        async saveStepData(stepIndex) {
            let payload = {}

            switch(stepIndex) {
                case 1: // Company Info
                    payload = {
                        company: {
                            country: this.formData.companyInfo.country,
                            vat_number: this.formData.companyInfo.pib,
                            legal_name: this.formData.companyInfo.legalName,
                            company_name: this.formData.companyInfo.companyName,
                            address: this.formData.companyInfo.companyAddress,
                            city: this.formData.companyInfo.companyCity,
                            zip_code: this.formData.companyInfo.companyPostalCode,
                            phone: this.isSerbian ? `+381${this.formData.companyInfo.companyPhone}` : this.formData.companyInfo.companyPhone,
                            email: this.formData.companyInfo.companyEmail,
                            legal_representative: this.formData.companyInfo.legalRepresentative,
                            additional_info: {
                                registration_number: this.formData.companyInfo.mb,
                                activity_code: this.formData.companyInfo.activityCode,
                                pdv: this.formData.companyInfo.pdv
                            }
                        }
                    }
                    break
                case 2: // Store Configuration
                    const selectedDesc = this.merchantDescriptionOptions.find(
                        opt => opt.value === this.formData.merchantDescription
                    )
                    payload = {
                        general: { store_name: this.formData.storeName },
                        base_domain: `${this.formData.storeUrl}.${this.shopDomain}`
                    }
                    if (selectedDesc) {
                        payload.merchant_description = selectedDesc.text
                        payload.mcc = selectedDesc.mcc
                    }
                    break
                case 3: // Product Type & Shipping
                    payload = {
                        product_types: [this.formData.productType],
                        settings: {
                            dev: {
                                cart: {
                                    shipping_methods: {
                                        methods: this.formData.shippingMethods.map(id => {
                                            const sm = this.shippingMethods.find(m => m.value === id)
                                            return { id, name: sm ? (this.formData.language === 'sr' ? sm.text.sr : sm.text.en) : id }
                                        })
                                    }
                                }
                            }
                        }
                    }
                    break
                case 4: // Payment Methods
                    let ppProviders = {}
                    if (this.formData.paymentMethods.includes('credit_card')) {
                        if (this.formData.creditCardProvider === 'monri' || this.formData.creditCardProvider === 'chipcard') {
                            ppProviders = { options: { id: 'monri', name: 'Monri' } }
                        }
                    }
                    payload = {
                        settings: {
                            dev: {
                                cart: {
                                    payment_methods: {
                                        methods: this.formData.paymentMethods.map(id => {
                                            const names = {
                                                'credit_card': this.formData.language === 'sr' ? 'Platna kartica' : 'Credit Card',
                                                'bank_transfer': this.formData.language === 'sr' ? 'Uplata na račun' : 'Bank Transfer',
                                                'cash_on_delivery': this.formData.language === 'sr' ? 'Plaćanje pouzećem' : 'Cash on Delivery'
                                            }
                                            return { id, name: names[id] || id }
                                        })
                                    },
                                    payment_providers: ppProviders
                                }
                            }
                        }
                    }
                    break
                case 5: // Documentation
                    if (this.formData.termsOfService) {
                        payload = {
                            documentation: {
                                terms_of_service: this.formData.termsOfService
                            }
                        }
                    }
                    break
            }

            if (Object.keys(payload).length === 0) return

            try {
                await this.$integrationApi.patch('/ecomm/user', payload)
            } catch (err) {
                console.warn('Failed to save step data:', err)
            }
        },

        async fetchTosPreview() {
            this.loadingTosPreview = true
            this.tosPreviewHtml = ''

            try {
                const payload = {
                    company: {
                        country: this.formData.companyInfo.country,
                        vat_number: this.formData.companyInfo.pib,
                        legal_name: this.formData.companyInfo.legalName,
                        company_name: this.formData.companyInfo.companyName,
                        address: this.formData.companyInfo.companyAddress,
                        city: this.formData.companyInfo.companyCity,
                        zip_code: this.formData.companyInfo.companyPostalCode,
                        phone: this.isSerbian ? `+381${this.formData.companyInfo.companyPhone}` : this.formData.companyInfo.companyPhone,
                        email: this.formData.companyInfo.companyEmail
                    },
                    settings: {
                        cart: {
                            payment_methods: {
                                methods: this.formData.paymentMethods.map(id => {
                                    const names = {
                                        'credit_card': this.formData.language === 'sr' ? 'Platna kartica' : 'Credit Card',
                                        'bank_transfer': this.formData.language === 'sr' ? 'Uplata na račun' : 'Bank Transfer',
                                        'cash_on_delivery': this.formData.language === 'sr' ? 'Plaćanje pouzećem' : 'Cash on Delivery'
                                    }
                                    return { id, name: names[id] || id }
                                })
                            }
                        },
                        price: {
                            default_currency: {
                                id: this.formData.currency,
                                name: this.formData.currency
                            }
                        }
                    }
                }

                const resp = await this.$integrationApi.post('/ecomm/terms-of-service/preview', payload)
                this.tosPreviewHtml = resp.data.html || ''
            } catch (err) {
                console.error('Failed to fetch TOS preview:', err)
                this.tosPreviewHtml = ''
            } finally {
                this.loadingTosPreview = false
            }
        },

        async validateDomain() {
            if (!this.formData.storeUrl) {
                this.domainValidationError = null
                this.domainValidationSuccess = false
                return
            }

            this.validatingDomain = true
            this.domainValidationError = null
            this.domainValidationSuccess = false

            try {
                const fullDomain = `${this.formData.storeUrl}.${this.shopDomain}`
                await this.$integrationApi.post('/ecomm/base-domain/check', {
                    domain: fullDomain
                })
                // If successful, domain is available
                this.domainValidationError = null
                this.domainValidationSuccess = true
            } catch (err) {
                // Domain is not available or validation failed
                this.domainValidationSuccess = false
                if (err.response && err.response.data && err.response.data.message) {
                    this.domainValidationError = err.response.data.message
                } else {
                    this.domainValidationError = this.$t('ecommerce.store.domainUnavailable')
                }
            } finally {
                this.validatingDomain = false
            }
        },

        prevStep() {
            if (this.currentStep > 0) {
                this.currentStep--
                this.saveToCache()
            }
        },

        addCustomDomain() {
            if (this.newDomain && !this.formData.customDomains.includes(this.newDomain)) {
                this.formData.customDomains.push(this.newDomain)
                this.newDomain = ''
                this.saveToCache()
            }
        },

        removeDomain(index) {
            this.formData.customDomains.splice(index, 1)
            this.saveToCache()
        },

        saveToCache() {
            const cacheData = {
                currentStep: this.currentStep,
                formData: this.formData,
                timestamp: new Date().toISOString()
            }
            localStorage.setItem(ONBOARDING_CACHE_KEY, JSON.stringify(cacheData))
        },

        getCachedData() {
            try {
                const cached = localStorage.getItem(ONBOARDING_CACHE_KEY)
                if (cached) {
                    return JSON.parse(cached)
                }
            } catch (err) {
                console.error('Failed to parse cached data:', err)
            }
            return null
        },

        clearCache() {
            localStorage.removeItem(ONBOARDING_CACHE_KEY)
        },

        handleFinish() {
            const isPartner = this.paymentsIntegration && this.paymentsIntegration.data && this.paymentsIntegration.data.partner === 'monri'
            if (isPartner) {
                this.showChipcardModal = true
            } else {
                this.completeOnboarding()
            }
        },

        handleChipcardConfirm() {
            this.showChipcardModal = false
            this.completeOnboarding()
        },

        async completeOnboarding() {
            this.loading = true

            try {
                // Prepare the dev/live settings object
                const languageLabel = this.languageOptions.find(l => l.value === this.formData.language)

                // Compute payment_providers: chipcard and monri are the same company — always use monri
                let paymentProviders = {}
                if (this.formData.paymentMethods.includes('credit_card')) {
                    if (this.formData.creditCardReceivingMethod === 'chip_card' || this.formData.creditCardProvider === 'chipcard' || this.formData.creditCardProvider === 'monri') {
                        paymentProviders = { options: { id: 'monri', name: 'Monri' } }
                    }
                }

                const devSettings = {
                    maintenance: false,
                    header: {
                        logo: {
                            position: 1,
                            src: this.formData.logoBase64,
                            alt: 'Store Logo',
                            width: 120,
                            height: 40
                        }
                    },
                    cart: {
                        payment_methods: {
                            methods: this.formData.paymentMethods.map(id => {
                                const names = {
                                    'credit_card': this.formData.language === 'sr' ? 'Platna kartica' : 'Credit Card',
                                    'bank_transfer': this.formData.language === 'sr' ? 'Uplata na račun' : 'Bank Transfer',
                                    'cash_on_delivery': this.formData.language === 'sr' ? 'Plaćanje pouzećem' : 'Cash on Delivery'
                                }
                                return { id, name: names[id] || id }
                            })
                        },
                        shipping_methods: {
                            methods: this.formData.shippingMethods.map(id => {
                                const sm = this.shippingMethods.find(m => m.value === id)
                                return {
                                    id,
                                    name: sm ? (this.formData.language === 'sr' ? sm.text.sr : sm.text.en) : id
                                }
                            })
                        },
                        payment_providers: paymentProviders
                    },
                    price: {
                        default_currency: {
                            id: this.formData.currency,
                            name: this.formData.currency
                        }
                    },
                    i18n: {
                        default_locale: {
                            id: this.formData.language,
                            name: languageLabel ? languageLabel.text : this.formData.language
                        },
                        locales: this.languageOptions.map(l => ({
                            id: l.value,
                            name: l.text
                        }))
                    }
                }

                // Get selected merchant description with MCC codes
                const selectedMerchantDesc = this.merchantDescriptionOptions.find(
                    opt => opt.value === this.formData.merchantDescription
                )

                // Prepare the settings payload
                const settingsPayload = {
                    general: {
                        store_name: this.formData.storeName
                    },
                    base_domain: `${this.formData.storeUrl}.${this.shopDomain}`,
                    product_types: [this.formData.productType],
                    ...(selectedMerchantDesc && {
                        merchant_description: selectedMerchantDesc.text,
                        mcc: selectedMerchantDesc.mcc,
                        ...(selectedMerchantDesc.risk && { risk_level: selectedMerchantDesc.risk })
                    }),
                    company: {
                        country: this.formData.companyInfo.country,
                        vat_number: this.formData.companyInfo.pib,
                        legal_name: this.formData.companyInfo.legalName,
                        company_name: this.formData.companyInfo.companyName,
                        address: this.formData.companyInfo.companyAddress,
                        city: this.formData.companyInfo.companyCity,
                        zip_code: this.formData.companyInfo.companyPostalCode,
                        phone: this.isSerbian ? `+381${this.formData.companyInfo.companyPhone}` : this.formData.companyInfo.companyPhone,
                        email: this.formData.companyInfo.companyEmail,
                        legal_representative: this.formData.companyInfo.legalRepresentative,
                        additional_info: {
                            registration_number: this.formData.companyInfo.mb,
                            activity_code: this.formData.companyInfo.activityCode,
                            pdv: this.formData.companyInfo.pdv
                        }
                    },
                    settings: {
                        dev: devSettings,
                        live: devSettings
                    }
                }

                // Log the prepared payload for inspection
                console.log('Onboarding Request Payload:', JSON.stringify(settingsPayload, null, 2))
                console.log('Form Data:', JSON.stringify(this.formData, null, 2))

                // Save to ecommerce API
                await this.$integrationApi.patch('/ecomm/user', settingsPayload)

                // Save Monri credentials if provider is Monri and user entered credentials
                if (this.formData.creditCardProvider === 'monri' && !this.isMonriPartner &&
                    this.formData.monriMerchantKey && this.formData.monriAuthenticityToken) {
                    await this.$integrationApi.post('/payments/monri/settings', {
                        merchant_key: this.formData.monriMerchantKey,
                        authenticity_token: this.formData.monriAuthenticityToken
                    })
                }

                // Explicitly complete onboarding
                await this.$integrationApi.post('/ecomm/user/complete-onboarding')

                // Clear cache and emit success
                this.validationErrors = {}
                this.clearCache()
                this.$emit('onboardingSuccess', 'integration', 'protokol-ecommerce')
            } catch (err) {
                console.error('Onboarding error:', err)
                const resp = err.response && err.response.data
                if (resp && resp.code === 2000 && resp.errors) {
                    this.validationErrors = resp.errors
                    const messages = Object.values(resp.errors).reduce(function(acc, msgs) {
                        return acc.concat(msgs)
                    }, [])
                    this.$bvToast.toast(messages.join('\n'), {
                        variant: 'danger',
                        title: this.$t('validationError'),
                        noAutoHide: true
                    })
                } else {
                    this.validationErrors = {}
                    this.$bvToast.toast(this.$t('unexpectedError'), {
                        variant: 'danger',
                        title: this.$t('error')
                    })
                }
            } finally {
                this.loading = false
            }
        },

        getPaymentMethodName(id) {
            const method = this.paymentMethods.find(m => m.value === id)
            return method ? method.text : id
        },

        async loadMonriSettings() {
            try {
                const resp = await this.$integrationApi.get('/payments/monri/settings')
                const { settings } = resp.data || {}
                if (settings) {
                    this.formData.monriMerchantKey = settings.merchant_key || ''
                    this.formData.monriAuthenticityToken = settings.authenticity_token || ''
                }
            } catch (err) {
                // Settings may not exist yet — that's fine
            }
        },

        getShippingMethodName(id) {
            const method = this.shippingMethods.find(m => m.value === id)
            return method ? this.$t(method.textKey) : id
        },

        getPaymentMethodLabel(method) {
            const keys = {
                'credit_card': 'ecommerce.payment.paymentCard',
                'bank_transfer': 'ecommerce.payment.bankTransfer',
                'cash_on_delivery': 'ecommerce.payment.cashOnDelivery'
            }
            return keys[method] ? this.$t(keys[method]) : method
        },

        getPaymentMethodIcon(method) {
            const icons = {
                'credit_card': 'credit-card',
                'bank_transfer': 'bank',
                'cash_on_delivery': 'cash'
            }
            return icons[method] || 'currency-dollar'
        },

        getShippingMethodLabel(method) {
            const shippingMethod = this.shippingMethods.find(m => m.value === method)
            return shippingMethod ? this.$t(shippingMethod.textKey) : method
        },

        getShippingMethodIcon(method) {
            const icons = {
                'delivery': 'truck',
                'pickup': 'shop'
            }
            return icons[method] || 'box-seam'
        },

        getProductTypeLabel(type) {
            const productType = this.productTypes.find(p => p.value === type)
            return productType ? this.$t(productType.textKey) : type
        },

        getProductTypeIcon(type) {
            const icons = {
                'physical': 'box-seam',
                'digital': 'file-earmark-code',
                'service': 'tools'
            }
            return icons[type] || 'box-seam'
        },

        getCreditCardReceivingLabel() {
            if (this.formData.creditCardReceivingMethod === 'chip_card') {
                return 'Chipcard'
            } else if (this.formData.creditCardReceivingMethod === 'bank_transfer') {
                return this.$t('ecommerce.payment.bank')
            }
            return ''
        }
    }
}
</script>

<style scoped lang="scss">
.ecommerce-onboarding {
    min-height: 100vh;
}

.progress-bar-container {
    margin-bottom: 2rem;
}

.progress-steps {
    display: flex;
    justify-content: space-between;
    position: relative;
    
    &::before {
        content: '';
        position: absolute;
        top: 20px;
        left: 0;
        right: 0;
        height: 2px;
        background: var(--border-default);
        z-index: 0;
    }
}

.progress-step {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1;
    position: relative;
    z-index: 1;
    
    .step-circle {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        background: var(--form-bg);
        border: 2px solid var(--border-default);
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 600;
        color: var(--text-muted);
        margin-bottom: 0.5rem;
        transition: all 0.3s ease;
    }
    
    .step-label {
        font-size: 0.75rem;
        color: var(--text-muted);
        text-align: center;
        max-width: 100px;
        word-wrap: break-word;
    }
    
    &.active .step-circle {
        background: var(--primary-start);
        border-color: var(--primary-start);
        color: white;
    }
    
    &.completed .step-circle {
        background: #10b981;
        border-color: #10b981;
        color: white;
    }
    
    &.active .step-label,
    &.completed .step-label {
        color: var(--text-primary);
        font-weight: 500;
    }
}

.onboarding-card {
    background: var(--form-bg);
    border: 1px solid var(--border-default);
    border-radius: 12px;
    padding: 2rem;
}

.step-icon {
    color: var(--primary-start);
}

.step-title {
    color: var(--text-primary);
    font-weight: 600;
    margin-bottom: 0.5rem;
}

.step-description {
    color: var(--text-secondary);
    font-size: 1rem;
}

.features-list {
    max-width: 500px;
    margin: 0 auto;
}

.feature-item {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem;
    background: var(--bg-card);
    border-radius: 8px;
    margin-bottom: 0.75rem;
    color: var(--text-primary);
    
    svg {
        flex-shrink: 0;
    }
}

.custom-domains-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.domain-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 1rem;
    background: var(--bg-card);
    border: 1px solid var(--border-default);
    border-radius: 8px;
    color: var(--text-primary);
    
    span {
        word-break: break-all;
    }
}

.category-checkbox,
.payment-checkbox,
.shipping-checkbox {
    padding: 0.75rem;
    background: var(--bg-card);
    border-radius: 8px;
    border: 1px solid var(--border-default);
    transition: all 0.2s ease;
    
    &:hover {
        border-color: var(--primary-start);
    }

    ::v-deep .custom-control {
        padding-left: 1.5rem;
    }

    ::v-deep .custom-control-label {
        margin-left: 30px;
    }
}

.payment-method-item {
    margin-bottom: 0.5rem;
}

.summary-section {
    h5 {
        color: var(--text-primary);
        font-weight: 600;
        margin-bottom: 0.75rem;
    }
}

.summary-list {
    list-style: none;
    padding: 0;
    
    li {
        padding: 0.5rem 0;
        color: var(--text-secondary);
        border-bottom: 1px solid var(--border-default);
        
        &:last-child {
            border-bottom: none;
        }
        
        strong {
            color: var(--text-primary);
        }
    }
}

.summary-logo {
    max-width: 200px;
    max-height: 100px;
    object-fit: contain;
    border: 1px solid var(--border-default);
    border-radius: 8px;
    padding: 0.5rem;
    background: var(--bg-card);
}

// Preview/Summary step styles
.preview-success-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: rgba(var(--success-rgb, 40, 167, 69), 0.1);
}

.preview-store-banner {
    display: flex;
    align-items: center;
    gap: 1.5rem;
    background: var(--bg-secondary);
    border: 1px solid var(--border-default);
    border-radius: 12px;
    padding: 1.25rem 1.5rem;
}

.preview-store-banner-logo {
    flex-shrink: 0;
    width: 72px;
    height: 72px;
    border-radius: 10px;
    border: 1px solid var(--border-default);
    background: var(--bg-card);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
}

.preview-banner-logo-img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: 4px;
}

.preview-banner-logo-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
}

.preview-store-banner-info {
    flex: 1;
    min-width: 0;
}

.preview-store-name {
    font-size: 1.1rem;
    font-weight: 700;
    color: var(--text-primary);
    margin-bottom: 0.25rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.preview-store-url-link {
    font-size: 0.875rem;
    color: var(--primary-start);
    text-decoration: none;
    word-break: break-all;

    &:hover {
        text-decoration: underline;
    }
}

.preview-store-badges {
    display: flex;
    flex-wrap: wrap;
    gap: 0.375rem;
}

.preview-cards-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
}

.preview-card {
    background: var(--bg-card);
    border: 1px solid var(--border-default);
    border-radius: 12px;
    overflow: hidden;

    &--full {
        grid-column: 1 / -1;
    }
}

.preview-card-header {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    padding: 0.875rem 1.25rem;
    border-bottom: 1px solid var(--border-default);
    background: var(--bg-secondary);

    h6 {
        margin: 0;
        font-weight: 600;
        font-size: 0.9rem;
        color: var(--text-primary);
    }
}

.preview-card-header-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    background: rgba(var(--primary-start-rgb), 0.1);
    flex-shrink: 0;
}

.preview-card-body {
    padding: 0.75rem 1.25rem;
}

.preview-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.5rem 0;
    border-bottom: 1px solid var(--border-default);

    &:last-child {
        border-bottom: none;
        padding-bottom: 0;
    }

    &:first-child {
        padding-top: 0;
    }
}

.preview-label {
    font-size: 0.8rem;
    font-weight: 500;
    color: var(--text-muted);
    white-space: nowrap;
    padding-top: 2px;
    flex-shrink: 0;
    max-width: 45%;
}

.preview-value {
    font-size: 0.875rem;
    color: var(--text-primary);
    font-weight: 500;
    text-align: right;
    word-break: break-word;

    &--sm {
        font-size: 0.8rem;
    }
}

.preview-mono {
    font-family: monospace;
    font-size: 0.85rem;
    color: var(--text-secondary);
}

.preview-doc-badge {
    font-size: 0.75rem;
    font-weight: 500;
    white-space: nowrap;
}

.preview-method-item {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.5rem 0;
    border-bottom: 1px solid var(--border-default);

    &:last-child {
        border-bottom: none;
        padding-bottom: 0;
    }

    &:first-child {
        padding-top: 0;
    }
}

.preview-method-icon {
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    background: rgba(var(--primary-start-rgb), 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 2px;
}

.preview-method-content {
    flex: 1;
    min-width: 0;
}

.preview-method-name {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--text-primary);
    display: block;
}

.preview-method-sub {
    margin-top: 0.25rem;
    line-height: 1.5;
}

.document-preview {
    .preview-box {
        border: 1px solid var(--border-default);
        border-radius: 6px;
        padding: 2rem;
        background-color: var(--bg-secondary);
        text-align: center;
        min-height: 150px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
    }
    
    .dashed-box {
        border: 2px dashed var(--border-default);
        background-color: var(--form-bg);
        padding: 2rem;
    }
    
    .upload-box {
        border: 2px dashed var(--primary-start);
        background-color: var(--form-bg);
        cursor: pointer;
        transition: all 0.3s ease;
        padding: 2rem;
        
        &:hover {
            border-color: var(--primary-end);
            background-color: rgba(var(--primary-start-rgb), 0.05);
        }
    }
    
    h5 {
        color: var(--text-primary);
        font-weight: 600;
        margin-bottom: 0.75rem;
    }
}

.navigation-buttons {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    padding-top: 1.5rem;
    border-top: 1px solid var(--border-default);
}

.help-link {
    color: var(--text-muted);
    font-size: 0.875rem;
    text-decoration: none;
    transition: color 0.2s ease;
    
    &:hover {
        color: var(--primary-start);
        text-decoration: underline;
    }
}

// Fix input-group-prepend alignment with input field
::v-deep .input-group {
    align-items: stretch;
    margin-bottom: 0;
    
    .input-group-prepend {
        margin-bottom: 0;
        
        .input-group-text {
            display: flex;
            align-items: center;
            margin-bottom: 0;
            border-width: 1px;
        }
    }
    
    .form-control {
        margin-bottom: 0;
        border-width: 1px;
    }
}

// Ensure consistent border width across all form controls
::v-deep .form-control,
::v-deep .custom-select,
::v-deep textarea.form-control {
    border-width: 1px;
}

::v-deep .custom-file-label {
    border-width: 1px;
}

.bank-accounts-container {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 1rem;
    margin-top: 0.5rem;
}

.payment-type-selection {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    margin-top: 0.5rem;
}

.payment-type-box {
    padding: 2rem 1.5rem;
    border: 2px solid var(--border-default);
    border-radius: 12px;
    background: var(--bg-card);
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    min-height: 140px;
    
    &:hover {
        border-color: var(--primary-start);
        background: rgba(var(--primary-start-rgb), 0.05);
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
    
    &.selected {
        border-color: var(--primary-start);
        background: rgba(var(--primary-start-rgb), 0.1);
        box-shadow: 0 0 0 3px rgba(var(--primary-start-rgb), 0.1);
    }
    
    &.disabled {
        opacity: 0.6;
        cursor: not-allowed;
        
        &:hover {
            border-color: var(--primary-start);
            background: rgba(var(--primary-start-rgb), 0.1);
            transform: none;
            box-shadow: 0 0 0 3px rgba(var(--primary-start-rgb), 0.1);
        }
    }
    
    .b-icon {
        color: var(--primary-start);
    }
    
    .payment-type-name {
        color: var(--text-primary);
        font-weight: 600;
        font-size: 1rem;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.5rem;
    }
}

.payment-card-logos {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.35rem;
    margin-top: 0.25rem;
}

.payment-logo-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 28px;
    min-width: 52px;
    padding: 0 0.45rem;
    border-radius: 6px;
    background: #ffffff;
    border: 1px solid rgba(0, 0, 0, 0.08);
}

.payment-logo-pill--compact {
    min-width: 46px;
}

.payment-card-logo {
    max-height: 18px;
    max-width: 44px;
    width: auto;
    height: auto;
    object-fit: contain;
}

.provider-logo {
    max-width: 130px;
    width: 100%;
    max-height: 44px;
    height: auto;
    object-fit: contain;
}

.provider-logo--wide {
    max-width: 170px;
}

.powered-by-footer {
    text-align: center;
    border-top: 1px solid var(--border-default);
    
    .powered-by-text {
        font-size: 0.75rem;
        color: var(--text-secondary);
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 0.5rem;
    }
    
    .powered-by-logo {
        max-width: 120px;
        height: auto;
        opacity: 0.8;
    }
}

.bank-account-box {
    padding: 1rem;
    border: 2px solid var(--border-default);
    border-radius: 8px;
    background: var(--bg-card);
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover {
        border-color: var(--primary-start);
        background: rgba(var(--primary-start-rgb), 0.05);
    }
    
    &.selected {
        border-color: var(--primary-start);
        background: rgba(var(--primary-start-rgb), 0.1);
        box-shadow: 0 0 0 3px rgba(var(--primary-start-rgb), 0.1);
    }
    
    .bank-account-header {
        display: flex;
        align-items: center;
        color: var(--text-primary);
        font-size: 0.95rem;
        margin-bottom: 0.5rem;
    }
    
    .bank-account-number {
        color: var(--text-secondary);
        font-family: monospace;
        font-size: 0.9rem;
        margin-bottom: 0.5rem;
    }
    
    .bank-account-status {
        display: flex;
        justify-content: flex-end;
    }
}

.tos-preview-frame {
    width: 100%;
    height: 500px;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 0.5rem;
    background: #0a0a0a;
}

@media (max-width: 768px) {
    .progress-steps {
        flex-wrap: wrap;
    }

    .progress-step {
        flex-basis: 50%;
        margin-bottom: 1rem;
    }

    .onboarding-card {
        padding: 1.5rem;
    }
}
</style>

<style lang="scss">
/* Fix z-index for modals in onboarding */
.ecommerce-onboarding {
    .modal-backdrop {
        z-index: 1050 !important;
    }
    
    .modal {
        z-index: 1055 !important;
    }
}
</style>
